// ============================================================
// W'ILY'S PIER — PLAYER HOME  (THE TROPHY WING)
// ────────────────────────────────────────────────────────────
// Every player's personal wing of the amusement park. An INSTANCED
// room you enter from the Pier: your deeds from EVERY IE game made
// physical — trophies on plinths, paintings on the walls, medallions
// on the shelves. Walk up to any one and inspect it. Locked deeds sit
// as empty pedestals: the chase, standing in your own hall.
//
// ── HOW INSTANCING WORKS (the whole trick) ───────────────────
//   Presence lives at  homes/{ownerUid}/players/{uid}.
//   The room you're standing in is identified by ownerUid — NOT by
//   the viewer. So:
//     • Enter your own home        → ownerUid = you        → your hall.
//     • A friend follows/visits    → ownerUid = you        → they join
//                                     YOUR instance and see YOUR trophies.
//     • A friend enters ALONE      → their app sets ownerUid = them
//                                     → their own hall, their own trophies.
//   Same code, the uid IS the instance key. This mirrors the private-room
//   pattern in MMOSystem (zones/{zone}/rooms/{code}) — here the "code" is
//   just the owner's uid, so every player gets a permanent private room
//   for free.
//
// ── WHERE THE TROPHIES COME FROM ─────────────────────────────
//   The registry (achievements.js) is the single source of truth. Every
//   achievement becomes a trophy object, placed into the wing that matches
//   its game namespace (Terror wing, Casino wing, …) and ranked WITHIN that
//   wing by rarity. Add an achievement anywhere in the registry and a new
//   trophy appears in the correct wing automatically — zero code here.
//
//   Owner's deeds are read live via useAchievements(ownerUid): earned ones
//   glow with rarity radiance; unearned ones are dim empty pedestals (only
//   in your OWN home — a visitor sees a clean hall of earned trophies only).
//
// ── SAFETY / LAWS ────────────────────────────────────────────
//   • READ-ONLY on achievements. Never writes a deed. Reads solely via
//     useAchievements(ownerUid) — the canonical shared source.
//   • Presence writes mirror the Pier's exact payload shape (name, x, y,
//     facingX, facingY, gender, companionId, ts) — no new Firebase fields,
//     so nothing to whitelist under beta rules (auth != null).
//   • Uses the same engine primitives as the Pier (PlayerEntity, camera,
//     ProximityBubble, MobileDpad) — no new engine code.
//   • The detailed 2D catalog is delegated to AchievementBadgeBook, which
//     was purpose-built for viewingSelf={false} against any uid.
//
// ── USAGE ────────────────────────────────────────────────────
//   <PierHome
//     ownerUid={targetUid}          // whose hall this is (you OR a friend)
//     ownerName={targetName}
//     viewerUid={playerUid}         // who is standing in it
//     viewerName={playerName}
//     profile={liveProfile}         // viewer's profile (sprite/companion)
//     onLeave={() => backToPier()}
//   />
// ============================================================

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ref, set, onValue, remove, update } from "firebase/database";
import { db } from "./firebase";
import { setSocialHidden } from "./ieSocial";   // see THE SUPPRESSION SIGNAL below
import { SD } from "./theme";
import { PlayerEntity, usePlayerAnimation, MobileDpad, isMobile } from "./PlayerEntity";
import { ProximityBubble, getNearby } from "./ProximitySystem";
import { calcCamOffset, isPortrait as engineIsPortrait } from "./ZoneEngine";
import { useAchievements } from "./useAchievements";
import {
  ACHIEVEMENTS,
  GAME_NAMESPACES,
  ACHIEVEMENT_RARITY_META,
  getAchievementDisplay,
  allAchievementIds,
} from "./achievements";
import AchievementBadgeBook from "./AchievementBadgeBook";
import { listenToGallery, getMint, PIECE_PROVENANCE } from "./bazaar";
// ⭐ OW-24 · FURNISH THE INSIDE. The Hall is the second room of a
// holding, and it shares ONE engine, ONE catalogue, ONE shed and ONE
// Firebase node with the yard — `homes/{ownerUid}/minerium/props`,
// filtered to `area === "hall"`. Nothing here is a second system; it is
// a second RENDERER over the same data (PIERRE_PRIME L24).
import {
  listenToHolding, emptyHolding, propsInArea, shedFor,
  resolvePlacement, inHall,
  placeProp, pickUpProp, rotateProp, moveProp, rotStepFor,
  growthOf, harvest, bindServerClock, serverNow,
  reachTile, ghostStateFor, blockedByProp, nextAim, tilesUnder, dimsOf, quarterRot, hallCentre,
} from "./minerium";
import { ITEMS, AREAS, KIND, SHELF_META, LAYER, layerOf, moodOf, FOUNDING_LIGHT } from "./mineriumCatalogue";
import { listenToStock, listenToOwned, foundHolding, wearItem } from "./mineriumShop";
import { MineriumProp2D, auditProp2D } from "./mineriumProps2D";
// ⭐ (2026-09-15) the build chrome the yard uses — the same shed, the same
// undo, the same first-visit card, the same sounds. One build UI, two rooms.
import { isTypingTarget, GHOST_VERB, useUndo, useBursts, ShedPanel, OnboardCard, Thumb, ABOVE_SHED_MOBILE, markOnboarded } from "./buildKit";
import { sfx } from "./pierSfx";
import { UI, uiBtn, uiLabel } from "./theme";
// ⭐ THE 3D HALL — a renderer swap, the Pier's own pattern (see `is3D`).
import Hall3D from "./Hall3D";
import {
  composeHall, pinHall, normaliseInterior, interiorCellOk, deedSpotOk, buildCells, moveDeed, resetDeeds,
  finishAllowed, interiorKey, TOOL, BUILD_COST_UD,
  sealingWalls, normaliseDesigns, saveDesign, clearDesign, applyDesign, applyLayout, applyPlain, copyHallFrom,
  DESIGN_SLOTS, LAST_SLOT, COPIED_SLOT, DESIGN_NAME_MAX, planOf,
} from "./hallInterior";
import { LayoutMinimap, InteriorMinimap } from "./hallLayoutThumb";
import { rotateInput, resetGroundForward } from "./pierCamera";

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isPortrait = () => engineIsPortrait() && isMobile();

const T = { FLOOR: 0, WALL: 1, VOID: 2, WALLTROPHY: 3, FRAME: 4 };

// ⭐ THE HALL IS 3D — the same call the Pier made (UnstablePierre.js,
// `is3D`). `Hall3D.jsx` draws this file's map, wings, trophies, frames,
// Deed Book, yard door and furniture; every rule, key, write and modal
// below stays here and does not know which renderer is mounted.
// ⚠ NOT DEAD CODE. The 2D renderer below (HallBackdrop, HallFloor,
// TrophyVisual, FrameVisual, the ProximityBubbles…) is unreferenced while
// this is true. It is the reference the 3D hall is checked against and one
// boolean from being live if the 3D hall ever has to be stood down in a
// hurry. Do not delete it on "nothing renders it."
const is3D = true;

// The 3D interaction pill's skin — the Pier's `pill3D`, so a prompt does
// not change species when you walk through the Hall's door.
const hallPill3D = (color) => ({
  background: `linear-gradient(135deg, ${color}22, rgba(4,2,14,.95))`,
  border: `1px solid ${color}88`, borderRadius: 8,
  padding: "7px 14px", cursor: "pointer",
  color, fontSize: 10, letterSpacing: 1.5,
  fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
  boxShadow: `0 0 14px ${color}33, 0 6px 18px #000a`,
});

// Presence cap inside a home. Trivially bumpable to 8 for an "open house".
const HOME_CAP = 4;

const GHOST_TTL    = 15000;
const HEARTBEAT_MS = 5000;
const SYNC_MS      = 80;

function calcTile() {
  const vh = window.innerHeight;
  if (isPortrait()) return 40;
  const byH = Math.floor(vh / 14);
  return Math.max(36, Math.min(64, byH));
}

// ─────────────────────────────────────────────────────────────
// KEYFRAMES — imperative + id-guarded, matching the pier's discipline.
// The old inline <style> block risked duplicate injection on every render
// (the "J'aeonxi trap"). This installs the Hall's keyframes exactly once,
// keyed by a stable id, and is a no-op on every subsequent mount.
// ─────────────────────────────────────────────────────────────
function ensureHallKeyframes() {
  if (typeof document === "undefined") return;
  const ID = "pier-hall-keyframes";
  if (document.getElementById(ID)) return;
  const el = document.createElement("style");
  el.id = ID;
  el.textContent = `
    @keyframes bubbleFloat { from{transform:translateX(-50%) translateY(0px)} to{transform:translateX(-50%) translateY(-5px)} }
  `;
  document.head.appendChild(el);
}

// ─────────────────────────────────────────────────────────────
// THE WINGS
// One alcove per IE game namespace. Order runs west→east along the hall;
// the empire meta capstone gets the place of honor at the far end.
// Each wing declares its floor-tile footprint (drawn as FLOOR), the wall
// row where paintings hang, and where its plinth row and shelf row sit.
//
// A wing is defined once here; trophies flow into it from the registry.
// Add a namespace to GAME_NAMESPACES and a wing entry here → new alcove.
// ─────────────────────────────────────────────────────────────
const WING_ORDER = ["terror", "casino", "sol", "pier", "empire"];

// Geometry: hall is a long west→east corridor. Each wing is a WING_W-wide bay.
// cols grow with the number of wings so the hall always fits every game.
//
// A bay is banded vertically so no single trophy class can ever overflow off
// the edge — each class gets a GRID (rows × width) and trophies serpentine
// through it. If a wing holds more of a class than one row fits, it simply
// flows onto the next row of that band. Nothing is ever dropped.
const WING_W = 10;                       // interior width of one wing bay
const GUTTER = 1;                        // wall between wings

const HALL_TOP  = 2;                     // y of the north wall (the gallery wall)
// Paintings hang on the single north wall row. Any overflow past one row per
// wing flows DOWN onto a walkable "easel" row on the floor (so every framed
// deed is reachable, and nothing is ever dropped).
const PAINTING_WALL_ROWS = [HALL_TOP];             // on the wall
const PAINTING_EASEL_ROWS = [HALL_TOP + 1];        // floor easels for overflow
const PAINTING_ROWS = [...PAINTING_WALL_ROWS, ...PAINTING_EASEL_ROWS];
// Plinth band: grand free-standing pedestals, up to 2 floor rows.
const PLINTH_ROWS   = [HALL_TOP + 3, HALL_TOP + 5];
// Medallion band: shelves near the south wall, up to 2 floor rows.
const MEDAL_ROWS    = [HALL_TOP + 8, HALL_TOP + 7];

// Floor spans from the easel row down to just above the south wall.
const FLOOR_TOP = HALL_TOP + 1;          // first walkable row (easel row)
const FLOOR_BOT = HALL_TOP + 9;          // last walkable row
const HALL_BOT  = FLOOR_BOT + 1;         // y of the south wall
// The row where furniture starts drawing in FRONT of furniture behind
// it. Derived, so it follows the floor if the hall is ever made deeper.
const FLOOR_MID = Math.floor((FLOOR_TOP + FLOOR_BOT) / 2);

const ROWS = HALL_BOT + 2;               // +border

// Build wing x-origins left→right.
const WINGS = (() => {
  const out = [];
  let x = 2; // leave a west entry gutter
  for (const game of WING_ORDER) {
    if (!GAME_NAMESPACES[game]) continue;
    out.push({
      game,
      label: (GAME_NAMESPACES[game].label || game).toUpperCase(),
      tint: GAME_NAMESPACES[game].tint,
      x0: x,                 // first interior col
      x1: x + WING_W - 1,    // last interior col
      cx: x + Math.floor(WING_W / 2),
    });
    x += WING_W + GUTTER;
  }
  return out;
})();

const COLS = (WINGS.length ? WINGS[WINGS.length - 1].x1 : WING_W) + 3;

const WING_BY_GAME = Object.fromEntries(WINGS.map((w) => [w.game, w]));

// Spawn: west end of the hall, centered vertically.
const SPAWN = { x: 1, y: Math.floor((FLOOR_TOP + FLOOR_BOT) / 2) };

// ─────────────────────────────────────────────────────────────
// TROPHY PLACEMENT
// Turn the registry into placed trophy objects. Each achievement lands in
// the wing matching its game (unknown games fold into the empire wing), is
// classified by rarity into painting / plinth / medallion, and is dealt a
// slot along that class's row within the wing. Higher rarity gets the
// centermost, most prominent slots.
//
// Deterministic: same registry → same layout every visit, for everyone.
// ─────────────────────────────────────────────────────────────
function rarityOrder(id) {
  const a = ACHIEVEMENTS[id];
  return ACHIEVEMENT_RARITY_META[a?.rarity]?.order ?? 0;
}

// class by rarity: mythic/legendary → plinth (grandest),
// epic/rare → painting (wall of renown), uncommon/common → medallion (shelf).
function trophyClass(id) {
  const o = rarityOrder(id);
  if (o >= 4) return "plinth";
  if (o >= 2) return "painting";
  return "medallion";
}

// Deal a run of ids into a grid: `rows` (an ordered list of y's) × the wing's
// interior width. Fills the first row left→right, then the next, etc. This is
// the anti-overflow guarantee: a class with more items than one row holds
// simply flows onto the next row of its band, and only truly runs out if it
// exceeds rows×width (with the last row absorbing any final overflow so an id
// is NEVER dropped).
function gridSlots(ids, x0, rows, width) {
  const out = [];
  ids.forEach((id, i) => {
    let rowIdx = Math.floor(i / width);
    if (rowIdx >= rows.length) rowIdx = rows.length - 1; // absorb overflow on last row
    const col = rowIdx === rows.length - 1
      ? i - rowIdx * width            // remaining offset on the absorbing row
      : i % width;
    const x = x0 + Math.min(col, width - 1);
    out.push({ id, x, y: rows[rowIdx] });
  });
  return out;
}

function buildTrophies() {
  // Group ids by wing, then by class, sorted by rarity (desc) then name so the
  // grandest deed of each class fills the most prominent (first) slot.
  const byWing = {};
  for (const w of WINGS) byWing[w.game] = { plinth: [], painting: [], medallion: [] };

  for (const id of allAchievementIds()) {
    const a = ACHIEVEMENTS[id];
    if (!a) continue;
    const wingGame = WING_BY_GAME[a.game] ? a.game : "empire";
    if (!byWing[wingGame]) continue; // empire wing must exist for fallback
    byWing[wingGame][trophyClass(id)].push(id);
  }

  const sortRank = (a, b) =>
    (rarityOrder(b) - rarityOrder(a)) ||
    (ACHIEVEMENTS[a].name < ACHIEVEMENTS[b].name ? -1 : 1);

  const placed = [];
  for (const w of WINGS) {
    const groups = byWing[w.game];
    if (!groups) continue;

    gridSlots([...groups.plinth].sort(sortRank), w.x0, PLINTH_ROWS, WING_W)
      .forEach((s) => placed.push({ ...s, kind: "plinth", wing: w.game, tint: w.tint }));

    gridSlots([...groups.painting].sort(sortRank), w.x0, PAINTING_ROWS, WING_W)
      .forEach((s) => placed.push({ ...s, kind: "painting", wing: w.game, tint: w.tint }));

    gridSlots([...groups.medallion].sort(sortRank), w.x0, MEDAL_ROWS, WING_W)
      .forEach((s) => placed.push({ ...s, kind: "medallion", wing: w.game, tint: w.tint }));
  }
  return placed;
}
const TROPHIES = buildTrophies();

// ─────────────────────────────────────────────────────────────
// FRAME SLOTS — M1: FRAMES IN THE HALL (the original spark)
// ────────────────────────────────────────────────────────────
// The Hall of Deeds gets picture frames: decor slots where owned gallery
// pieces hang as physical art in your instanced home. The north wall is
// the Wall of Renown (trophies). The SOUTH wall becomes the Gallery Wall —
// a band of empty frames per wing where your Bazaar art hangs.
//
//   ── SCHEMA (the whole seam, and nothing else) ──────────────
//   homes/{ownerUid}/decor/{slotId} = pieceId    (a string, or absent)
//   A frame is a READ-ONLY view into the gallery. It stores only the
//   pieceId; the art itself is joined live from profiles/{ownerUid}/gallery
//   + bazaar/mints. A piece that's LISTED (escrowed) simply isn't in the
//   gallery, so its frame renders empty — no coupling with the marketplace,
//   no coupling with trophy geometry. Zero writes to achievements, zero new
//   presence fields. Only the decor node is new.
//
// Slots are laid out per wing, serpentining along that wing's south-wall
// band exactly like the trophy grid — a new IE game gets a wing, and the
// wing comes with its frame band for free (the "one entry adds everything"
// law the portals follow).
// ─────────────────────────────────────────────────────────────
const FRAMES_PER_WING = 4;                 // gallery slots per game bay (bump-anytime)
const FRAME_WALL_ROW  = HALL_BOT;          // the south wall becomes the Gallery Wall

// Deal FRAMES_PER_WING slots across each wing's interior width, centered so
// the band reads as an intentional gallery run rather than crowding one edge.
function buildFrameSlots() {
  const out = [];
  for (const w of WINGS) {
    const n = FRAMES_PER_WING;
    // center the run of n frames within the wing's interior [x0..x1]
    const startPad = Math.max(0, Math.floor((WING_W - n) / 2));
    for (let i = 0; i < n; i++) {
      const x = w.x0 + Math.min(startPad + i, WING_W - 1);
      out.push({
        slotId: `${w.game}_${i}`,   // stable id → deterministic decor key
        x,
        y: FRAME_WALL_ROW,
        wing: w.game,
        tint: w.tint,
      });
    }
  }
  return out;
}
const FRAME_SLOTS = buildFrameSlots();
const FRAME_SLOT_BY_ID = Object.fromEntries(FRAME_SLOTS.map((s) => [s.slotId, s]));
// You inspect / hang a south-wall frame from the floor tile directly ABOVE it
// (you stand in the hall, facing the south wall).
function frameStandTileY() { return FRAME_WALL_ROW - 1; }

// ─────────────────────────────────────────────────────────────
// MAP
// Floor across all wing bays, walls between them and around the edge,
// WALLTROPHY tiles on the north painting row (non-walkable, but they read
// as a gallery wall you stand in front of).
// ─────────────────────────────────────────────────────────────
function buildMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.VOID));

  // Carve the central hall corridor (spine) full width.
  for (let r = FLOOR_TOP; r <= FLOOR_BOT; r++)
    for (let c = 1; c < COLS - 1; c++) map[r][c] = T.FLOOR;

  // North wall — the single gallery wall row that paintings hang on.
  for (const pr of PAINTING_WALL_ROWS) {
    for (let c = 0; c < COLS; c++) map[pr][c] = T.WALL;
  }
  // South wall of the hall.
  for (let c = 0; c < COLS; c++) map[HALL_BOT][c] = T.WALL;

  // East/west end walls (top wall → south wall).
  const topWall = Math.min(...PAINTING_WALL_ROWS);
  for (let r = topWall; r <= HALL_BOT; r++) {
    map[r][0] = T.WALL;
    map[r][COLS - 1] = T.WALL;
  }

  // Wall-row painting slots become WALLTROPHY (a framed spot on the wall).
  // Easel-row paintings stay on FLOOR (you walk right up to them).
  const wallRowSet = new Set(PAINTING_WALL_ROWS);
  for (const t of TROPHIES) {
    if (t.kind === "painting" && wallRowSet.has(t.y) && map[t.y]?.[t.x] !== undefined) {
      map[t.y][t.x] = T.WALLTROPHY;
    }
  }

  // South wall gallery frames — carve FRAME cells into the south wall
  // (non-walkable; you stand in the hall in front of them, like north-wall
  // paintings). These are the M1 picture-frame slots.
  for (const s of FRAME_SLOTS) {
    if (map[s.y]?.[s.x] !== undefined) map[s.y][s.x] = T.FRAME;
  }

  return map;
}
// ⚠ OW-42: this is the Hall AS BUILT. The Hall you walk is `curHall()` —
// this map with the owner's walls, doorways and moved deeds on it.
const MAP = buildMap();

function isWalkable(x, y) {
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  return curHall().map[y][x] === T.FLOOR;
}

// Where the player stands to inspect a trophy. A painting mounted ON the wall
// is inspected from the floor tile directly below it; everything else (plinths,
// medallions, easel paintings) is inspected by stepping onto its own tile.
const WALL_ROW_SET = new Set(PAINTING_WALL_ROWS);
function standTileY(trophy) {
  if (trophy.kind === "painting" && WALL_ROW_SET.has(trophy.y)) return trophy.y + 1;
  return trophy.y;
}

// The Deed Book pedestal — opens the full catalog. Sits dead center.
const DEED_BOOK = {
  id: "deed_book",
  x: Math.floor(COLS / 2),
  y: FLOOR_BOT,
  rx: 1, ry: 1,
};


// ─────────────────────────────────────────────────────────────
// RADIANCE — rarer earned deeds glow louder (mirrors the BadgeBook ladder)
// ─────────────────────────────────────────────────────────────
function radianceFor(id) {
  const o = rarityOrder(id);
  if (o >= 4) return 3; // legendary/mythic
  if (o >= 2) return 2; // rare/epic
  return 1;
}

// ─────────────────────────────────────────────────────────────
// BACKDROP — the AIR of the room. Not open sky anymore: this is the
// warm interior atmosphere your hall breathes in. A deep-wood wall wash
// up top, a soft lamplit floor haze below, a hearth glow banked at the
// west end, and slow dust motes turning in the sconce light. The old
// starfield survives only as a faint dusting seen through the high
// clerestory windows — a house on a pier, under a night sky.
// ─────────────────────────────────────────────────────────────
function HallBackdrop({ tile }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;

    // Dust motes drifting in lamplight — sparse, warm, slow.
    const motes = Array.from({ length: 70 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.4 + 0.4,
      a: Math.random() * 0.5 + 0.15,
      driftX: (Math.random() - 0.5) * 0.12,
      driftY: -(Math.random() * 0.08 + 0.02),
      speed: Math.random() * 0.0016 + 0.0007,
      phase: Math.random() * Math.PI * 2,
    }));
    // A faint night sky, seen only through the top clerestory band.
    const clerestoryH = H * 0.14;
    const stars = Array.from({ length: 90 }, () => ({
      x: Math.random() * W, y: Math.random() * clerestoryH,
      r: Math.random() * 1.1 + 0.2,
      a: Math.random() * 0.7 + 0.15,
      speed: Math.random() * 0.002 + 0.0008,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── GOD-RAYS — the signature. Shafts of cool moonlight angling down
    //    from the high clerestory windows onto the gallery floor. A hall of
    //    deeds at night is defined by this single image. One shaft per
    //    window opening; each drifts and breathes slightly out of phase, so
    //    the light feels alive without ever being busy. Seeded once.
    const RAY_COUNT = 5;
    const rays = Array.from({ length: RAY_COUNT }, (_, i) => {
      const cx = W * ((i + 0.5) / RAY_COUNT);       // window center across the top
      return {
        originX: cx,
        width: W * (0.05 + Math.random() * 0.03),   // shaft half-width at the floor
        lean: (Math.random() - 0.5) * W * 0.10,      // horizontal drift toward the floor
        baseA: 0.05 + Math.random() * 0.03,          // peak opacity
        speed: 0.006 + Math.random() * 0.004,        // breathing rate
        phase: Math.random() * Math.PI * 2,
        driftAmp: W * 0.012,                         // slow sway of the whole shaft
        driftSpeed: 0.003 + Math.random() * 0.002,
      };
    });

    // Hearth glow anchored near the west entry (spawn side).
    const hearthX = W * 0.06, hearthY = H * 0.58;

    let frame = 0, raf;
    function draw() {
      // 1) The wall wash — deep warm wood up high, sinking to a lamplit
      //    parlour glow near the floor. This is what replaces the void.
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0.00, "#0a0713");   // shadowed ceiling
      g.addColorStop(0.16, "#141019");   // upper wall, wood-dark
      g.addColorStop(0.45, "#1c1518");   // mid wall, warming
      g.addColorStop(0.72, "#221812");   // lower wall, lamp-touched
      g.addColorStop(1.00, "#0c0a10");   // floor shadow
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      // 2) Clerestory night sky — a slim lighter band along the top with
      //    a few stars, as if through high windows. Kept subtle.
      const sky = ctx.createLinearGradient(0, 0, 0, clerestoryH * 1.4);
      sky.addColorStop(0, "rgba(20,16,40,0.55)");
      sky.addColorStop(1, "transparent");
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, clerestoryH * 1.4);
      stars.forEach((s) => {
        const a = s.a * (0.4 + 0.6 * Math.sin(frame * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230,220,255,${a * 0.7})`;
        ctx.fill();
      });

      const t = frame * 0.0016;

      // 3) Warm room-light pools — soft lamp glows that make the space
      //    feel candlelit rather than lit by a screen.
      [
        { x: W * 0.30, y: H * 0.42, r: W * 0.34, c: "rgba(201,168,76,0.08)" },
        { x: W * 0.66, y: H * 0.55, r: W * 0.30, c: "rgba(180,120,60,0.06)" },
        { x: W * 0.50, y: H * 0.20, r: W * 0.40, c: "rgba(124,90,180,0.05)" },
      ].forEach((n, i) => {
        const dx = Math.sin(t + i) * 18;
        const rg = ctx.createRadialGradient(n.x + dx, n.y, 0, n.x + dx, n.y, n.r);
        rg.addColorStop(0, n.c);
        rg.addColorStop(1, "transparent");
        ctx.fillStyle = rg;
        ctx.fillRect(0, 0, W, H);
      });

      // 3.5) GOD-RAYS — cool moonlight shafts from the clerestory windows.
      //      Drawn additively (lighter blend) so they read as light, not paint,
      //      and clipped to fade before the floor so they pool rather than
      //      streak. This is the room's signature at night.
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const rayBottom = H * 0.84;                       // rays fade out here (pool on the floor, clear of the south wall)
      rays.forEach((ry) => {
        const breathe = 0.55 + 0.45 * Math.sin(frame * ry.speed + ry.phase);
        const sway = Math.sin(frame * ry.driftSpeed + ry.phase) * ry.driftAmp;
        const topX = ry.originX + sway;
        const botX = ry.originX + ry.lean + sway;
        const topY = clerestoryH * 0.5;
        const a = ry.baseA * breathe;

        // Build the shaft as a 4-point polygon: narrow at the window,
        // wider at the floor, with a vertical gradient that fades top+bottom.
        const grad = ctx.createLinearGradient(0, topY, 0, rayBottom);
        grad.addColorStop(0.00, `rgba(150,170,230,0)`);
        grad.addColorStop(0.18, `rgba(150,170,230,${a})`);
        grad.addColorStop(0.70, `rgba(140,160,220,${a * 0.5})`);
        grad.addColorStop(1.00, `rgba(140,160,220,0)`);
        ctx.fillStyle = grad;

        const topHalf = ry.width * 0.35;
        const botHalf = ry.width;
        ctx.beginPath();
        ctx.moveTo(topX - topHalf, topY);
        ctx.lineTo(topX + topHalf, topY);
        ctx.lineTo(botX + botHalf, rayBottom);
        ctx.lineTo(botX - botHalf, rayBottom);
        ctx.closePath();
        ctx.fill();

        // a brighter core line down the middle of the shaft
        const coreGrad = ctx.createLinearGradient(0, topY, 0, rayBottom);
        coreGrad.addColorStop(0, `rgba(200,215,255,0)`);
        coreGrad.addColorStop(0.2, `rgba(200,215,255,${a * 0.6})`);
        coreGrad.addColorStop(0.8, `rgba(190,205,250,0)`);
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.moveTo(topX - topHalf * 0.35, topY);
        ctx.lineTo(topX + topHalf * 0.35, topY);
        ctx.lineTo(botX + botHalf * 0.35, rayBottom);
        ctx.lineTo(botX - botHalf * 0.35, rayBottom);
        ctx.closePath();
        ctx.fill();

        // a soft pool where the shaft meets the floor
        const poolR = ry.width * 1.6;
        const pool = ctx.createRadialGradient(botX, rayBottom, 0, botX, rayBottom, poolR);
        pool.addColorStop(0, `rgba(150,170,230,${a * 0.5})`);
        pool.addColorStop(1, "transparent");
        ctx.fillStyle = pool;
        ctx.beginPath();
        ctx.ellipse(botX, rayBottom, poolR, poolR * 0.35, 0, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();

      // 4) The hearth — a banked ember glow at the west end that breathes.
      const flick = 0.75 + 0.25 * Math.sin(frame * 0.09) + 0.08 * Math.sin(frame * 0.23);
      const hr = W * 0.22 * flick;
      const hg = ctx.createRadialGradient(hearthX, hearthY, 0, hearthX, hearthY, hr);
      hg.addColorStop(0, `rgba(230,120,40,${0.14 * flick})`);
      hg.addColorStop(0.4, `rgba(180,70,30,${0.07 * flick})`);
      hg.addColorStop(1, "transparent");
      ctx.fillStyle = hg;
      ctx.fillRect(0, 0, W, H);

      // 5) Dust motes turning in the light. Motes that drift through a
      //    moonlight shaft catch it and flare — the dust-in-a-sunbeam effect
      //    that makes the god-rays read as volumetric rather than flat.
      motes.forEach((m) => {
        m.x += m.driftX; m.y += m.driftY;
        if (m.y < 0) { m.y = H; m.x = Math.random() * W; }
        if (m.x < 0) m.x = W; if (m.x > W) m.x = 0;
        const a = m.a * (0.5 + 0.5 * Math.sin(frame * m.speed + m.phase));

        // Is this mote inside a shaft's width at its current height? If so,
        // blend toward cool bright light and boost its size a touch.
        let catch_ = 0;
        if (m.y > clerestoryH && m.y < H * 0.84) {
          const prog = (m.y - clerestoryH * 0.5) / (H * 0.84 - clerestoryH * 0.5);
          for (const ry of rays) {
            const sway = Math.sin(frame * ry.driftSpeed + ry.phase) * ry.driftAmp;
            const cx = ry.originX + ry.lean * prog + sway;
            const halfW = (ry.width * 0.35) + (ry.width - ry.width * 0.35) * prog;
            const d = Math.abs(m.x - cx);
            if (d < halfW) { catch_ = Math.max(catch_, 1 - d / halfW); break; }
          }
        }
        const r = m.r * (1 + catch_ * 0.9);
        ctx.beginPath();
        ctx.arc(m.x, m.y, r, 0, Math.PI * 2);
        if (catch_ > 0) {
          // blend warm lamp dust → cool bright moon-dust
          const warm = [255, 225, 170], cool = [205, 220, 255];
          const mix = catch_;
          const cr = Math.round(warm[0] + (cool[0] - warm[0]) * mix);
          const cg = Math.round(warm[1] + (cool[1] - warm[1]) * mix);
          const cb = Math.round(warm[2] + (cool[2] - warm[2]) * mix);
          ctx.fillStyle = `rgba(${cr},${cg},${cb},${(a * 0.5) + catch_ * 0.5})`;
        } else {
          ctx.fillStyle = `rgba(255,225,170,${a * 0.5})`;
        }
        ctx.fill();
      });

      frame++;
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
    // ⚠⚠ `tile` IS A DEPENDENCY, and the empty array was a real bug.
    // `W`/`H` are captured from `canvas.width`/`height` at mount, but
    // React rewrites those ATTRIBUTES whenever `tile` changes — which
    // also blanks the canvas — while this still-running rAF keeps
    // painting to the OLD dimensions. Rotate a phone (`calcTile` flips
    // on `isPortrait()`) or resize the window and the room's atmosphere
    // is stuck at the previous size forever: a hard unpainted band down
    // the right and bottom of the hall, god-rays anchored to coordinates
    // that no longer line up, motes wrapping at the wrong edge. It never
    // recovers without a reload. `HallRoomCanvas` below already sets its
    // own width/height inside the effect and takes `tile`; this one did
    // not. Found by adversarial review 2026-09-13.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile]);
  return (
    <canvas
      ref={canvasRef}
      width={COLS * tile}
      height={ROWS * tile}
      style={{ position: "absolute", left: 0, top: 0, zIndex: 0 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// FLOOR — the ROOM ITSELF, drawn once onto a single canvas (like the
// Pier's PierPlanks). No more per-tile grid. A warm herringbone parquet
// floor on the walkable spine, a runner rug down the middle, wainscoted
// walls tinted with each wing's accent as *wallpaper* (not glowing tiles),
// crown molding at the ceiling line, baseboards where wall meets floor,
// and soft sconce pools washing the walls. Seeded, so it's the same room
// every visit — walk in and it's home.
//
// Signature preserved: HallFloor({ tile, pulse }). Mounts where it always
// did; `pulse` now drives only the gentle sconce breathing.
// ─────────────────────────────────────────────────────────────
function wingAt(x) {
  return WINGS.find((w) => x >= w.x0 && x <= w.x1) || null;
}

function hexToRgb(hex) {
  const h = (hex || "#888888").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

// Deterministic PRNG so the room's wood grain never reshuffles between visits.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The static room shell — drawn once per tile-size, cached to a canvas.
function HallRoomCanvas({ tile, hallKey }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = COLS * tile, H = ROWS * tile;
    canvas.width = W; canvas.height = H;
    const rnd = mulberry32(0x5EED ^ (tile * 131));

    ctx.clearRect(0, 0, W, H);

    // Helper: is this map cell floor / wall?
    const M = curHall().map;
    const isFloorCell = (r, c) => M[r]?.[c] === T.FLOOR;
    const isWallCell  = (r, c) => M[r]?.[c] === T.WALL || M[r]?.[c] === T.WALLTROPHY || M[r]?.[c] === T.FRAME;

    // ── 1) WALLS — wainscot + wallpaper, tinted per wing ──────────────
    // Draw a continuous wall band behind the top gallery row and the two
    // end columns, colored by whichever wing sits above/beside it.
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (!isWallCell(r, c)) continue;
        const x = c * tile, y = r * tile;
        const w = wingAt(c);
        const accent = w ? hexToRgb(w.tint) : hexToRgb(SD.purpleLight);

        // Base wall — warm plaster-dark.
        const wg = ctx.createLinearGradient(x, y, x, y + tile);
        wg.addColorStop(0, "#171017");
        wg.addColorStop(1, "#241a1c");
        ctx.fillStyle = wg;
        ctx.fillRect(x, y, tile, tile);

        // Wallpaper tint — a soft breath of the wing's color, very low.
        ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},0.10)`;
        ctx.fillRect(x, y, tile, tile);

        // The SOUTH wall is the Gallery Wall (M1 frames) — give it a cool
        // moonlit cast so it reads distinctly from the warm north Wall of
        // Renown. Warm trophies of deed to the north; cool-lit art to the
        // south. This encodes the room's core north/south split in the light.
        if (r === HALL_BOT) {
          const cool = ctx.createLinearGradient(x, y, x, y + tile);
          cool.addColorStop(0, "rgba(120,140,200,0.10)");
          cool.addColorStop(1, "rgba(90,110,170,0.04)");
          ctx.fillStyle = cool;
          ctx.fillRect(x, y, tile, tile);
        }

        // Faint damask stripes for texture (vertical), seeded jitter.
        ctx.strokeStyle = `rgba(${accent.r},${accent.g},${accent.b},0.06)`;
        ctx.lineWidth = 1;
        for (let s = 0.2; s < 1; s += 0.28) {
          const sx = x + tile * s + (rnd() - 0.5) * 2;
          ctx.beginPath(); ctx.moveTo(sx, y); ctx.lineTo(sx, y + tile); ctx.stroke();
        }

        // Wainscot panel on the lower ~40% of every wall tile (chair rail).
        const railY = y + tile * 0.6;
        ctx.fillStyle = "rgba(30,20,16,0.75)";
        ctx.fillRect(x, railY, tile, tile * 0.4);
        // chair-rail highlight
        ctx.strokeStyle = "rgba(201,168,76,0.16)";
        ctx.lineWidth = Math.max(1, tile * 0.03);
        ctx.beginPath(); ctx.moveTo(x, railY); ctx.lineTo(x + tile, railY); ctx.stroke();
        // panel inset outline
        ctx.strokeStyle = "rgba(0,0,0,0.4)";
        ctx.lineWidth = 1;
        ctx.strokeRect(x + tile * 0.14, railY + tile * 0.08, tile * 0.72, tile * 0.24);

        // inner shadow at wall edges for depth
        ctx.fillStyle = "rgba(0,0,0,0.28)";
        ctx.fillRect(x, y, tile, tile * 0.06);
      }
    }

    // ── 2) CROWN MOLDING — a gold-touched line along the top wall row ──
    const topWallY = (Math.min(...PAINTING_WALL_ROWS) + 1) * tile;
    const crownGrad = ctx.createLinearGradient(0, topWallY - tile * 0.14, 0, topWallY);
    crownGrad.addColorStop(0, "rgba(201,168,76,0.22)");
    crownGrad.addColorStop(1, "rgba(60,44,20,0.5)");
    ctx.fillStyle = crownGrad;
    ctx.fillRect(0, topWallY - tile * 0.14, W, tile * 0.14);

    // ── 3) FLOOR — herringbone parquet on every walkable tile ─────────
    // Compute the walkable footprint bounds.
    const plankL = Math.max(1, Math.floor(tile * 0.14));   // plank short side
    const woodBase = { r: 74, g: 48, b: 30 };              // warm walnut
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (!isFloorCell(r, c)) continue;
        const x = c * tile, y = r * tile;

        // base fill
        const shade = 0.86 + rnd() * 0.18;
        ctx.fillStyle = `rgb(${Math.round(woodBase.r * shade)},${Math.round(woodBase.g * shade)},${Math.round(woodBase.b * shade)})`;
        ctx.fillRect(x, y, tile, tile);

        // herringbone: alternate 45°/135° plank hatching per checker cell
        const flip = (r + c) % 2 === 0;
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, tile, tile); ctx.clip();
        ctx.strokeStyle = "rgba(0,0,0,0.22)";
        ctx.lineWidth = 1;
        const step = Math.max(4, tile / 4);
        for (let o = -tile; o < tile * 2; o += step) {
          ctx.beginPath();
          if (flip) { ctx.moveTo(x + o, y); ctx.lineTo(x + o + tile, y + tile); }
          else      { ctx.moveTo(x + o + tile, y); ctx.lineTo(x + o, y + tile); }
          ctx.stroke();
        }
        // a couple of lighter grain streaks for warmth
        ctx.strokeStyle = `rgba(150,105,60,${0.10 + rnd() * 0.06})`;
        for (let g = 0; g < 2; g++) {
          const o = rnd() * tile;
          ctx.beginPath();
          if (flip) { ctx.moveTo(x + o, y); ctx.lineTo(x + o + tile, y + tile); }
          else      { ctx.moveTo(x + o + tile, y); ctx.lineTo(x + o, y + tile); }
          ctx.stroke();
        }
        ctx.restore();

        // subtle plank seams on tile edges
        ctx.strokeStyle = "rgba(0,0,0,0.18)";
        ctx.strokeRect(x + 0.5, y + 0.5, tile - 1, tile - 1);
      }
    }

    // ── 4) THE RUNNER RUG — a long carpet down the central spine ──────
    // Runs the walkable rows, inset one tile from each side wall, with a
    // patterned border and a soft medallion field. Purely decorative; it
    // sits under the trophies and the player.
    const rugTop = (FLOOR_TOP + 1) * tile;
    const rugBot = (FLOOR_BOT) * tile;
    const rugL = 2 * tile;
    const rugR = (COLS - 2) * tile;
    const rugW = rugR - rugL, rugH = rugBot - rugTop;
    if (rugW > 0 && rugH > 0) {
      // field
      const rg = ctx.createLinearGradient(0, rugTop, 0, rugBot);
      rg.addColorStop(0, "rgba(60,20,30,0.78)");
      rg.addColorStop(0.5, "rgba(80,26,38,0.72)");
      rg.addColorStop(1, "rgba(55,18,28,0.78)");
      ctx.fillStyle = rg;
      ctx.fillRect(rugL, rugTop, rugW, rugH);

      // outer gold border bands
      ctx.strokeStyle = "rgba(201,168,76,0.5)";
      ctx.lineWidth = Math.max(2, tile * 0.06);
      ctx.strokeRect(rugL + tile * 0.18, rugTop + tile * 0.18, rugW - tile * 0.36, rugH - tile * 0.36);
      ctx.strokeStyle = "rgba(201,168,76,0.22)";
      ctx.lineWidth = 1;
      ctx.strokeRect(rugL + tile * 0.34, rugTop + tile * 0.34, rugW - tile * 0.68, rugH - tile * 0.68);

      // running diamond motif along the border
      ctx.fillStyle = "rgba(201,168,76,0.28)";
      const dm = tile * 0.16;
      for (let dx = rugL + tile * 0.6; dx < rugR - tile * 0.4; dx += tile * 0.7) {
        for (const dy of [rugTop + tile * 0.5, rugBot - tile * 0.5]) {
          ctx.beginPath();
          ctx.moveTo(dx, dy - dm); ctx.lineTo(dx + dm, dy);
          ctx.lineTo(dx, dy + dm); ctx.lineTo(dx - dm, dy); ctx.closePath();
          ctx.fill();
        }
      }
      // central medallion
      const mcx = (rugL + rugR) / 2, mcy = (rugTop + rugBot) / 2;
      const mr = Math.min(rugW, rugH) * 0.28;
      const med = ctx.createRadialGradient(mcx, mcy, 0, mcx, mcy, mr);
      med.addColorStop(0, "rgba(201,168,76,0.20)");
      med.addColorStop(0.6, "rgba(124,58,140,0.14)");
      med.addColorStop(1, "transparent");
      ctx.fillStyle = med;
      ctx.beginPath(); ctx.ellipse(mcx, mcy, mr, mr * 0.7, 0, 0, Math.PI * 2); ctx.fill();
      // fringe suggestion at rug ends
      ctx.strokeStyle = "rgba(201,168,76,0.3)";
      ctx.lineWidth = 1;
      for (let fx = rugL + 4; fx < rugR; fx += 6) {
        ctx.beginPath(); ctx.moveTo(fx, rugTop - 3); ctx.lineTo(fx, rugTop); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(fx, rugBot); ctx.lineTo(fx, rugBot + 3); ctx.stroke();
      }
    }

    // ── 5) BASEBOARD — a warm skirting where the top wall meets floor ─
    const baseY = (Math.min(...PAINTING_WALL_ROWS) + 1) * tile;
    ctx.fillStyle = "rgba(40,28,20,0.9)";
    ctx.fillRect(0, baseY, W, tile * 0.1);
    ctx.strokeStyle = "rgba(201,168,76,0.14)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, baseY + tile * 0.1); ctx.lineTo(W, baseY + tile * 0.1); ctx.stroke();

    // ── 6) FLOOR VIGNETTE — corners fall into shadow, center stays lit ─
    const vg = ctx.createRadialGradient(W / 2, H * 0.6, H * 0.2, W / 2, H * 0.6, H * 0.9);
    vg.addColorStop(0, "transparent");
    vg.addColorStop(1, "rgba(0,0,0,0.4)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile, hallKey]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", left: 0, top: 0, zIndex: 1, pointerEvents: "none" }}
    />
  );
}

// Living light layer on top of the static room: breathing wall sconces
// between the wings + a warm floor-glow that answers `pulse`. Cheap DOM.
function HallSconces({ tile, pulse }) {
  // Place a sconce at each wing gutter along the top wall, plus one per
  // wing center — warm pools of light on the wallpaper.
  const sconceX = [];
  for (const w of WINGS) {
    sconceX.push(w.x0 - 0.5);          // gutter before the wing
    sconceX.push(w.cx);                // center of the wing
  }
  const wallY = Math.min(...PAINTING_WALL_ROWS);
  const glow = 0.6 + pulse * 0.4;
  return (
    <>
      {sconceX.map((sx, i) => (
        <div key={i} style={{
          position: "absolute",
          left: sx * tile - tile * 0.5,
          top: (wallY - 0.15) * tile,
          width: tile, height: tile * 1.1,
          zIndex: 2, pointerEvents: "none",
          background: `radial-gradient(ellipse at 50% 20%, rgba(255,190,90,${0.16 * glow}) 0%, rgba(255,150,60,${0.07 * glow}) 40%, transparent 72%)`,
        }}>
          {/* the sconce fixture itself */}
          <div style={{
            position: "absolute", left: "50%", top: tile * 0.12,
            transform: "translateX(-50%)",
            width: tile * 0.14, height: tile * 0.3,
            background: "linear-gradient(180deg, rgba(201,168,76,0.9), rgba(120,80,30,0.6))",
            borderRadius: "40% 40% 10% 10%",
            boxShadow: `0 0 ${6 + pulse * 8}px rgba(255,180,80,${0.6 * glow})`,
          }} />
        </div>
      ))}
    </>
  );
}

function HallFloor({ tile, pulse }) {
  return (
    <>
      <HallRoomCanvas tile={tile} hallKey={curHall().key} />
      <HallSconces tile={tile} pulse={pulse} />
      <HallPictureLights tile={tile} pulse={pulse} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PICTURE LIGHTS — AAA gallery lighting. Every wall-mounted art position
// (north-wall paintings + south-wall frames) gets a little brass hooded
// lamp casting a soft downward pool onto the piece and the wall behind it.
// The fixture is mounted whether or not art currently hangs, exactly like a
// real gallery. Cheap DOM, sits above the room canvas (z1) and below the
// pieces (z5) so art reads as lit-from-above. Warm on the north wall
// (trophies of renown), a touch cooler on the south wall (the art gallery).
// ─────────────────────────────────────────────────────────────
function HallPictureLights({ tile, pulse }) {
  const glow = 0.7 + pulse * 0.3;

  // Collect wall-mounted positions to light: north-wall paintings + frames.
  const lights = [];
  // North gallery wall — the painting slots (WALLTROPHY row).
  for (const t of curHall().trophies) {
    if (t.kind === "painting" && WALL_ROW_SET.has(t.y)) {
      lights.push({ x: t.x, y: t.y, warm: true, tint: t.tint, key: `pl-n-${t.x}-${t.y}` });
    }
  }
  // South gallery wall — every frame slot.
  for (const s of FRAME_SLOTS) {
    lights.push({ x: s.x, y: s.y, warm: false, tint: s.tint, key: `pl-s-${s.slotId}` });
  }

  return (
    <>
      {lights.map((L) => {
        // warm brass light on the north wall; cooler picture light on the south
        const beam = L.warm
          ? `rgba(255,196,110,${0.18 * glow})`
          : `rgba(220,225,255,${0.15 * glow})`;
        const beamMid = L.warm
          ? `rgba(255,160,70,${0.08 * glow})`
          : `rgba(200,210,255,${0.07 * glow})`;
        return (
          <div key={L.key} style={{
            position: "absolute",
            left: L.x * tile, top: (L.y - 0.42) * tile,
            width: tile, height: tile * 1.5,
            zIndex: 2, pointerEvents: "none",
          }}>
            {/* the downward light cone onto the piece */}
            <div style={{
              position: "absolute", left: "50%", top: tile * 0.28,
              transform: "translateX(-50%)",
              width: tile * 0.92, height: tile * 1.15,
              background: `radial-gradient(ellipse at 50% 0%, ${beam} 0%, ${beamMid} 42%, transparent 74%)`,
            }} />
            {/* the brass hood fixture, mounted above the frame */}
            <div style={{
              position: "absolute", left: "50%", top: tile * 0.14,
              transform: "translateX(-50%)",
              width: tile * 0.34, height: tile * 0.12,
              background: "linear-gradient(180deg, rgba(214,182,96,0.95), rgba(120,86,34,0.85))",
              borderRadius: "50% 50% 20% 20% / 80% 80% 20% 20%",
              boxShadow: `0 1px 3px rgba(0,0,0,0.5), 0 0 ${5 + pulse * 6}px ${L.warm ? "rgba(255,190,90," : "rgba(210,220,255,"}${0.5 * glow})`,
            }}>
              {/* the little arm connecting hood to wall */}
              <div style={{
                position: "absolute", left: "50%", top: -tile * 0.08,
                transform: "translateX(-50%)",
                width: tile * 0.04, height: tile * 0.1,
                background: "rgba(140,100,44,0.9)",
              }} />
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// WING BANNERS — the alcove's game name arched over its bay.
// ─────────────────────────────────────────────────────────────
function WingBanners({ tile }) {
  return (
    <>
      {WINGS.map((w) => (
        <div key={w.game} style={{
          position: "absolute",
          left: w.x0 * tile,
          top: (HALL_TOP - 0.9) * tile,
          width: WING_W * tile,
          textAlign: "center",
          fontSize: Math.max(9, tile * 0.24),
          letterSpacing: 3,
          fontFamily: SD.fontDisplay,
          fontWeight: 900,
          color: w.tint,
          textShadow: `0 0 10px ${w.tint}`,
          zIndex: 6, pointerEvents: "none", whiteSpace: "nowrap",
          opacity: 0.85,
        }}>{w.label}</div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FURNISHINGS SHINGLE — the seed of the house-building store.
// A hanging shop shingle mounted on the near (west) wall, in the same
// visual language as the Pier's boardwalk shingles. This pass it's a
// promise, not a shop: "FURNISHINGS · COMING SOON". When the store is
// wired, this becomes the doorway/kiosk that opens the decor catalog —
// the seam is here and nowhere else, so no other file has to change.
//
//   ── ✅ THE SEAM IS TAKEN — IT WENT TO THE MINERIUM ──────────
//   ⚠ AMENDED IN PLACE, because this comment used to describe a plan
//   that was not followed and a stale plan sends the next session to
//   build the wrong thing.
//
//   WHAT THIS COMMENT USED TO SAY: furnishings would share
//   `homes/{ownerUid}/decor/{slotId}` under a distinct slot-id prefix,
//   with a richer value ({ itemId, x, y }), rendered by a <PlacedDecor/>
//   layer beside <HallFloor/> and bought with ◆ DC.
//
//   WHAT WAS ACTUALLY BUILT, and why each part moved:
//   · The node is `homes/{ownerUid}/minerium/`, a SIBLING of decor, not a
//     child of it. HALL DECOR LAW (PIERRE_PRIME L21) says a frame stores
//     ONLY a pieceId — "a pointer, not a copy" — and growth timestamps,
//     rotations and stack parents do not belong under a law that says its
//     values are pointers. `useHallDecor` also builds `hungPieceIds` from
//     `Object.values(decorMap)`, which would collect those objects into a
//     Set of pieceId strings. Instancing and rules are identical either
//     way; both are under `homes/{ownerUid}` and owner-write.
//   · It is bought with ✹ UP DAWGS, not ◆ DC. THE ECONOMY LAW (L5) says
//     Crests buy ENDGAME ONLY and "never bread." A crop bed is bread.
//   · It renders in 3D (`Minerium3D.jsx`), not as a layer on this canvas,
//     because caP'n ruled the holding is 3D this session.
//
//   THE SHINGLE IS NOW THE DOOR, which is what this seam always said it
//   would become — it just leads outside instead of opening a catalogue.
//   Trophies, presence, frames and instancing are untouched, exactly as
//   promised.
// ─────────────────────────────────────────────────────────────
const FURNISHINGS_SHINGLE = {
  // Mounted on the west end wall, just north of spawn — the first thing
  // you see walking in. ⭐ NOW INTERACTIVE: it is the door to the yard.
  x: 1,
  y: HALL_TOP,
};

// The floor tile you stand on to go outside. One below the shingle, the
// same "stand in front of the thing" pattern the wall trophies and the
// gallery frames both use — so the Hall has exactly one interaction
// grammar and a player never has to learn a second one.
const YARD_DOOR = {
  id: "yard_door",
  x: FURNISHINGS_SHINGLE.x,
  y: HALL_TOP + 1,
  rx: 0, ry: 0,
};

// ⚠ OW-24 · TILES THE HALL ALREADY OWNS. `isWalkable` says these are
// floor, and they are — you stand on them to use the fixture standing
// there. A chair placed on top of the Deed Book or across the door out
// would be drawn over a working interactable and would read as a bug in
// the fixture rather than as furniture in the way.
// ⚠⚠ DERIVED, NOT NAMED (2026-09-15). It was two hand-listed tiles, and
// every plinth, medal stand and easel on the floor was left out — solid
// 3D geometry a chair could be put straight into. And the spawn tile,
// where every visitor arrives, could hold a Pale Pillar. Every floor
// fixture the Hall draws is reserved, with the reason a player reads.
// ⭐ OW-42 · THE HALL IS NOW THE OWNER'S. Deeds move, walls go up, so what
// is reserved and where the busts stand are the COMPOSED Hall's
// (`hallInterior.js`), not a list made once. The fixtures below are the
// part nothing moves: nothing is built on them, and nothing stands on them.
//   ⚑ the frames' stand tiles joined them (OW-42): a wall there would shut
//   a frame off, and a chair there always stood between you and the art.
function hallFixtures() {
  const m = new Map();
  for (const s of FRAME_SLOTS) m.set(`${s.x},${frameStandTileY()}`, "Stand here to look at the frame.");
  m.set(`${DEED_BOOK.x},${DEED_BOOK.y}`, "The Deed Book stands there.");
  m.set(`${YARD_DOOR.x},${YARD_DOOR.y}`, "That is the way outside.");
  m.set(`${SPAWN.x},${SPAWN.y}`, "That is the doorway in.");
  return m;
}
// ⚠ ONE OBJECT, ALWAYS (composeHall caches per base, by identity).
// Hoisted for the module-cycle reason getHallLayout gives below.
let _hallBase = null;
export function getHallBase() {
  if (_hallBase) return _hallBase;
  _hallBase = {
    map: MAP, cols: COLS, rows: ROWS, T,
    trophies: TROPHIES, fixtures: hallFixtures(), spawn: SPAWN,
    wallRows: PAINTING_WALL_ROWS, floorTop: FLOOR_TOP, floorBot: FLOOR_BOT,
    wings: WINGS.map((w) => ({ game: w.game, label: w.label, x0: w.x0, x1: w.x1 })),
    gutters: WINGS.slice(1).map((w) => w.x0 - 1),
  };
  return _hallBase;
}
// ⭐ THE HALL ON SCREEN. One Hall is shown at a time (yours, or the one you
// are visiting), so the walk, the reach and the build rules read it here.
// PierHome sets it from the holding it is showing; the preview sets it too.
let _curHall = null;
function curHall() { return _curHall || (_curHall = composeHall(getHallBase(), null)); }
export function setHallInterior(raw) {
  const c = composeHall(getHallBase(), raw);
  _curHall = c;
  pinHall(getHallBase(), c);
  return c;
}
export function currentHall() { return curHall(); }
const _nicheSets = new WeakMap();
const nicheSet = (c) => { let n = _nicheSets.get(c); if (!n) { n = new Set(c.nicheXs); _nicheSets.set(c, n); } return n; };
const GUTTER_COLS = new Set([...WINGS.map((w) => w.x0 - 1), ...(WINGS.length ? [WINGS[WINGS.length - 1].x1 + 1] : [])]);
// the doorways, and the rows either side of the great door where the west
// wall already carries its own sconces (hallBuild's WestWall)
const WEST_DOOR_ROWS = new Set([YARD_DOOR.y, YARD_DOOR.y + 1, SPAWN.y - 2, SPAWN.y - 1, SPAWN.y, SPAWN.y + 1, SPAWN.y + 2]);
export const WALL_SIDE_ROT = { N: 0, W: Math.PI / 2 };
export function hallWallSideAt(x, y) {
  if (!isWalkable(x, y)) return null;
  const c = curHall();
  if (y - 1 === HALL_TOP && c.map[y - 1]?.[x] === T.WALL && !GUTTER_COLS.has(x) && !nicheSet(c).has(x)) return "N";
  if (x === 1 && c.map[y]?.[0] === T.WALL && !WEST_DOOR_ROWS.has(y)) return "W";
  return null;
}
// the side a held hanging should take at a tile, or null
const wallRotAt = (x, y) => {
  const side = hallWallSideAt(Math.round(x), Math.round(y));
  return side ? WALL_SIDE_ROT[side] : null;
};

// the Hall's own floor rule for the engine's ghost: true, or the reason
// ⚑ OW-34: `ctx` = { itemId, layer, rot } from the engine.
//   floor — a rug may lie under a fixture (a carpet under the Deed Book is
//           the point of a carpet); it still needs floor under it.
//   wall  — only a plain stretch of wall, and facing the room.
export const hallFloorOk = (x, y, ctx = null) => {
  if (!isWalkable(x, y) || !inHall(x, y)) return "That is through a wall.";
  if (ctx?.layer === LAYER.FLOOR) return true;
  if (ctx?.layer === LAYER.WALL) {
    const side = hallWallSideAt(x, y);
    if (!side) return "Hang it on a plain stretch of wall — face the north wall, or the west end.";
    if (quarterRot(ctx.rot || 0) !== WALL_SIDE_ROT[side]) return "It has to face the room.";
  }
  return curHall().reserved.get(`${x},${y}`) || true;
};

// ⭐ OW-42 · would this hanging still hang in Hall `c`? (applyInterior asks)
export function hangingFits(c, p) {
  const prev = _curHall; _curHall = c;
  try {
    for (const [x, y] of tilesUnder(p.x, p.y, dimsOf(p.itemId, p.rot))) {
      const ok = hallFloorOk(x, y, { itemId: p.itemId, layer: LAYER.WALL, rot: p.rot });
      if (ok !== true) return ok;
    }
    return true;
  } finally { _curHall = prev; }
}

// ⭐ OW-34 · THE HALL STAYS OPEN. Partitions make rooms; they must never
// make a SEALED room. Before a solid piece lands, moves or turns, the floor
// is walked from the doorway in: the Deed Book, the way out to the yard and
// every wing must still be reachable, or it is refused with what it would
// shut off. `cand` = { propId?, itemId, x, y, rot } — the piece as it would
// stand (propId: the piece being moved, whose old spot no longer counts).
// Whole-tile walk, the same test the walk loop uses (blockedByProp at a
// tile centre). ⚑ Only SOLID stand pieces can close a way; rugs, hangings,
// arches and doors never do, so for them this is one early return.
// ⚑ (verify review) It compares BEFORE and AFTER: every floor tile you can
// reach now must still be reachable, except the tiles the piece itself now
// stands on. So a player cannot seal themselves or a corner into a pocket,
// and a Hall that is ALREADY sealed (an old layout) only refuses a change
// that makes it worse — never an unrelated one.
// ⚑ cached per Hall AND per props bag: a new wall is a new Hall
const _reachCache = new WeakMap();
function reachCacheFor(c) { let m = _reachCache.get(c); if (!m) { m = new WeakMap(); _reachCache.set(c, m); } return m; }
function reachableHall(props) {
  const open = (x, y) => isWalkable(x, y) && !blockedByProp(props, AREAS.HALL, x, y, 0.3);
  const seen = new Set();
  if (!open(SPAWN.x, SPAWN.y)) return seen;
  seen.add(`${SPAWN.x},${SPAWN.y}`);
  const queue = [[SPAWN.x, SPAWN.y]];
  for (let q = 0; q < queue.length; q++) {
    const [x, y] = queue[q];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy, k = `${nx},${ny}`;
      if (seen.has(k) || !open(nx, ny)) continue;
      seen.add(k); queue.push([nx, ny]);
    }
  }
  return seen;
}
export function hallStaysOpen(props, cand) {
  const it = ITEMS[cand?.itemId];
  if (!it || it.walkOver || layerOf(cand.itemId) !== LAYER.STAND) return true;
  const bag = props || {};
  const cache = reachCacheFor(curHall());
  let before = cache.get(bag);
  if (!before) { before = reachableHall(bag); cache.set(bag, before); }
  const sim = {};
  for (const [id, p] of Object.entries(bag)) if (p && id !== cand.propId) sim[id] = p;
  const self = { __cand: { propId: "__cand", itemId: cand.itemId, area: AREAS.HALL, x: cand.x, y: cand.y, rot: cand.rot || 0 } };
  sim.__cand = self.__cand;
  if (blockedByProp(self, AREAS.HALL, SPAWN.x, SPAWN.y, 0.3)) return "That is where everyone comes in.";
  const after = reachableHall(sim);
  const lost = [];
  for (const k of before) {
    if (after.has(k)) continue;
    const [x, y] = k.split(",").map(Number);
    if (blockedByProp(self, AREAS.HALL, x, y, 0.3)) continue;      // the piece's own tiles
    lost.push([x, y]);
  }
  if (!lost.length) return true;
  const near = (fx, fy) => lost.some(([x, y]) => Math.abs(x - fx) + Math.abs(y - fy) <= 1);
  if (near(DEED_BOOK.x, DEED_BOOK.y)) return "That would shut off the Deed Book.";
  if (near(YARD_DOOR.x, YARD_DOOR.y)) return "That would shut off the way out to your yard.";
  const wing = WINGS.find((w) => lost.some(([x]) => x >= w.x0 && x <= w.x1));
  return wing ? `That would wall off part of the ${wing.label} wing.` : "That would wall off part of your Hall.";
}

// ⚑ OW-42: a new key, so a player who dismissed the OW-34 card sees this one
const HALL_NEW_CARD = "hall_ow42";

// ⭐ THE HALL, READABLE FROM OUTSIDE THIS FILE — the data the 3D renderer
// (`Hall3D.jsx`) draws, and what the dev preview (`?preview=hall`) mounts
// with no login. Pure: no Firebase, no React, nothing live. HOISTED
// FUNCTIONS, not consts, for the module-cycle reason `getPierWorld()` in
// UnstablePierre.js gives — Hall3D imports Pier3D, which imports
// UnstablePierre, which imports this file.
// ⚠ Built once and returned by identity, because Hall3D is React.memo'd
// and a fresh object per render would reconcile the whole hall.
// ⭐ OW-42: one layout PER COMPOSED HALL, by identity. `shell` is the part
// that never changes (the room itself, the atlas of deeds), so a new wall
// rebuilds the walls and the deeds, never the stone of the room.
const _layouts = new WeakMap();
let _shell = null;
function hallShell() {
  if (_shell) return _shell;
  _shell = {
    map: MAP, cols: COLS, rows: ROWS, T,
    wings: WINGS.map((w) => ({ ...w, icon: GAME_NAMESPACES[w.game]?.icon || "◈" })),
    trophies: TROPHIES,
    frameSlots: FRAME_SLOTS,
    wallRows: PAINTING_WALL_ROWS,
    deedBook: DEED_BOOK,
    yardDoor: YARD_DOOR,
    spawn: SPAWN,
    hallTop: HALL_TOP, hallBot: HALL_BOT, floorTop: FLOOR_TOP, floorBot: FLOOR_BOT,
    // what a deed shows, earned or not — the renderer never reads the registry itself
    deedInfo: (id, earned) => {
      const d = getAchievementDisplay(id, earned);
      const r = ACHIEVEMENT_RARITY_META[ACHIEVEMENTS[id]?.rarity] || ACHIEVEMENT_RARITY_META.common;
      return { icon: d.icon, name: d.name, masked: !!d.masked, rarityTint: r.tint, order: r.order };
    },
  };
  return _shell;
}
// `hall` = a composed Hall (currentHall / setHallInterior); omitted → the one on screen
export function getHallLayout(hall = null) {
  const c = hall || curHall();
  let L = _layouts.get(c);
  if (L) return L;
  const shell = hallShell();
  L = {
    ...shell,
    shell,
    hall: c,
    map: c.map,
    trophies: c.trophies,
    nicheXs: c.nicheXs,
    // ⭐ OW-34 · the build hints: where a hanging may hang, and the tiles a
    // deed or fixture keeps for itself (lit while a piece is held)
    wallSpots: (() => {
      const prev = _curHall; _curHall = c;          // hallWallSideAt reads the Hall on screen
      const out = [];
      try {
        for (let y = FLOOR_TOP; y <= FLOOR_BOT; y++) for (let x = 1; x < COLS - 1; x++) {
          const side = hallWallSideAt(x, y);
          if (side) out.push({ x, y, side });
        }
      } finally { _curHall = prev; }
      return out;
    })(),
    reservedTiles: [...c.reserved.keys()].map((k) => k.split(",").map(Number)),
  };
  _layouts.set(c, L);
  return L;
}
export function isHallWalkable(x, y) { return isWalkable(x, y); }

function FurnishingsShingle({ tile, frame }) {
  const sway = Math.sin(frame * 0.03) * 1.4;   // gentle hanging sway
  const glow = 0.5 + 0.5 * Math.sin(frame * 0.05);
  const w = tile * 1.8, h = tile * 1.05;
  return (
    <div style={{
      position: "absolute",
      left: FURNISHINGS_SHINGLE.x * tile,
      top: (FURNISHINGS_SHINGLE.y - 0.1) * tile,
      width: w, height: h * 1.5,
      zIndex: 6, pointerEvents: "none",
      transformOrigin: "50% 0%",
      transform: `rotate(${sway * 0.4}deg)`,
    }}>
      {/* mounting bracket */}
      <div style={{
        position: "absolute", left: "50%", top: 0,
        transform: "translateX(-50%)",
        width: tile * 0.5, height: tile * 0.12,
        background: "linear-gradient(180deg, rgba(60,44,20,0.9), rgba(30,22,12,0.9))",
        borderRadius: 2,
      }} />
      {/* chains */}
      {[-1, 1].map((s) => (
        <div key={s} style={{
          position: "absolute", left: `${50 + s * 26}%`, top: tile * 0.1,
          width: 2, height: tile * 0.3,
          background: "repeating-linear-gradient(180deg, rgba(201,168,76,0.7) 0 3px, transparent 3px 5px)",
        }} />
      ))}
      {/* the shingle board */}
      <div style={{
        position: "absolute", left: "50%", top: tile * 0.4,
        transform: "translateX(-50%)",
        width: w, height: h,
        background: "linear-gradient(180deg, rgba(46,36,16,0.98), rgba(26,20,9,0.98))",
        border: `2px solid ${SD.gold}88`,
        borderRadius: 6,
        boxShadow: `0 4px 10px rgba(0,0,0,0.6), 0 0 ${8 + glow * 10}px ${SD.gold}33, inset 0 1px 0 ${SD.gold}22`,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 2,
      }}>
        <div style={{ fontSize: h * 0.34, filter: `drop-shadow(0 0 6px ${SD.gold})` }}>🌱</div>
        <div style={{
          fontSize: Math.max(7, h * 0.14), letterSpacing: 2,
          fontFamily: SD.fontDisplay, fontWeight: 900,
          color: SD.goldBright, textShadow: `0 0 8px ${SD.gold}`,
        }}>THE MINERIUM</div>
        <div style={{
          fontSize: Math.max(5, h * 0.10), letterSpacing: 2,
          fontFamily: SD.fontDisplay, color: SD.starDim,
          opacity: 0.7 + glow * 0.3,
        }}>✹ THIS WAY OUT</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TROPHY VISUAL — one placed deed, drawn by kind.
//   plinth    → a pedestal with a floating icon crystal + rarity glow
//   painting  → a framed portrait on the wall
//   medallion → a struck disc on the shelf
// Earned → lit + rarity radiance. Unearned → dim empty fixture (owner only).
// ─────────────────────────────────────────────────────────────
function TrophyVisual({ trophy, earned, tile, frame }) {
  const def = ACHIEVEMENTS[trophy.id];
  if (!def) return null;
  const display = getAchievementDisplay(trophy.id, earned);
  const rarity = ACHIEVEMENT_RARITY_META[def.rarity] || ACHIEVEMENT_RARITY_META.common;
  const rTint = rarity.tint;
  const r = earned ? radianceFor(trophy.id) : 0;
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.06 + trophy.x);

  const dim = { filter: "grayscale(0.85)", opacity: 0.32 };

  if (trophy.kind === "painting") {
    const onWall = WALL_ROW_SET.has(trophy.y);
    const w = tile * 0.9, h = tile * 1.05;
    return (
      <div style={{
        position: "absolute",
        left: trophy.x * tile + (tile - w) / 2,
        top: trophy.y * tile + tile * (onWall ? 0.02 : -0.06),
        width: w, height: h,
        zIndex: 5, pointerEvents: "none",
        ...(earned ? {} : dim),
      }}>
        <div style={{
          width: "100%", height: "100%",
          borderRadius: 4,
          border: `2px solid ${earned ? rTint : "#3a3350"}`,
          background: earned
            ? `radial-gradient(circle at 40% 30%, ${trophy.tint}44, rgba(6,3,18,0.95) 70%)`
            : "rgba(10,8,22,0.9)",
          boxShadow: earned ? `0 0 ${8 + pulse * 10}px ${rTint}66, inset 0 0 12px ${trophy.tint}33` : "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: h * 0.5,
          filter: earned ? `drop-shadow(0 0 4px ${rTint})` : "none",
        }}>{display.masked ? "✶" : display.icon}</div>
        {/* easel legs for floor-standing (overflow) paintings */}
        {!onWall && (
          <div style={{
            position: "absolute", left: "50%", bottom: -tile * 0.14,
            transform: "translateX(-50%)",
            width: w * 0.5, height: tile * 0.16,
            borderLeft: `2px solid ${earned ? rTint + "88" : "#2a2540"}`,
            borderRight: `2px solid ${earned ? rTint + "88" : "#2a2540"}`,
            borderRadius: "0 0 3px 3px",
          }} />
        )}
      </div>
    );
  }

  if (trophy.kind === "medallion") {
    const d = tile * 0.5;
    return (
      <div style={{
        position: "absolute",
        left: trophy.x * tile + (tile - d) / 2,
        top: trophy.y * tile + tile * 0.28,
        width: d, height: d,
        zIndex: 5, pointerEvents: "none",
        ...(earned ? {} : dim),
      }}>
        {/* shelf tick */}
        <div style={{
          position: "absolute", left: -d * 0.2, top: d * 1.05,
          width: d * 1.4, height: 2, background: "rgba(0,0,0,0.5)",
          borderRadius: 2,
        }} />
        <div style={{
          width: "100%", height: "100%", borderRadius: "50%",
          border: `2px solid ${earned ? rTint : "#3a3350"}`,
          background: earned
            ? `radial-gradient(circle at 38% 30%, ${trophy.tint}55, rgba(6,3,18,0.95) 68%)`
            : "rgba(10,8,22,0.85)",
          boxShadow: earned ? `0 0 ${5 + pulse * 6}px ${rTint}55` : "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: d * 0.55,
        }}>{display.masked ? "✶" : display.icon}</div>
      </div>
    );
  }

  // PLINTH — pedestal + floating crystal, the grandest fixture.
  const iconSize = tile * 0.62;
  return (
    <div style={{
      position: "absolute",
      left: trophy.x * tile,
      top: (trophy.y - 1) * tile,
      width: tile, height: tile * 2,
      zIndex: 5, pointerEvents: "none",
      ...(earned ? {} : dim),
    }}>
      {/* radiance halo */}
      {r >= 2 && earned && (
        <div style={{
          position: "absolute", left: "50%", top: tile * 0.35,
          width: tile * 1.3, height: tile * 1.3,
          transform: "translateX(-50%)",
          borderRadius: "50%",
          background: `conic-gradient(from ${frame * 2}deg, transparent, ${rTint}55, transparent 60%, ${trophy.tint}55, transparent)`,
          filter: "blur(3px)", opacity: 0.7,
        }} />
      )}
      {/* floating icon crystal */}
      <div style={{
        position: "absolute", left: "50%",
        top: tile * (0.45 - pulse * 0.08),
        transform: "translateX(-50%)",
        width: iconSize, height: iconSize,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: iconSize * 0.85,
        color: earned ? rTint : "#4a4360",
        filter: earned ? `drop-shadow(0 0 ${8 + pulse * 10}px ${rTint})` : "none",
      }}>{display.masked ? "✶" : display.icon}</div>
      {/* pedestal */}
      <div style={{
        position: "absolute", left: "50%", bottom: 0,
        transform: "translateX(-50%)",
        width: tile * 0.7, height: tile * 0.85,
        background: earned
          ? `linear-gradient(180deg, ${trophy.tint}33, rgba(8,4,20,0.95))`
          : "linear-gradient(180deg, rgba(30,26,48,0.8), rgba(8,6,18,0.95))",
        border: `1px solid ${earned ? rTint + "88" : "#2a2540"}`,
        borderRadius: "3px 3px 5px 5px",
        boxShadow: earned ? `0 0 ${6 + pulse * 8}px ${rTint}44` : "none",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FRAME VISUAL — one south-wall picture frame (M1).
//   empty  → a gilded empty frame. In your OWN hall it glows a soft invite
//            ("hang something here"); in a visitor's hall an empty frame is
//            just a quiet frame (no clutter, no chase-marks).
//   filled → the hung art, matted inside the frame, with a provenance-tinted
//            glow (earned outshines minted outshines bought).
// Purely presentational; it renders UNDER the player, ON the south wall.
// ─────────────────────────────────────────────────────────────
function FrameVisual({ slot, piece, isOwnHome, tile, frame }) {
  const w = tile * 0.86, h = tile * 0.92;
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.05 + slot.x);
  const filled = !!piece;
  const prov = piece?.prov;
  const glowC = filled ? (prov?.color || slot.tint) : slot.tint;

  return (
    <div style={{
      position: "absolute",
      left: slot.x * tile + (tile - w) / 2,
      top: slot.y * tile + (tile - h) / 2,
      width: w, height: h,
      zIndex: 5, pointerEvents: "none",
    }}>
      {/* the frame moulding */}
      <div style={{
        width: "100%", height: "100%",
        borderRadius: 3,
        padding: Math.max(2, tile * 0.06),
        background: filled
          ? `linear-gradient(145deg, ${SD.gold}dd, ${SD.gold}77 40%, #6a4c1e 100%)`
          : (isOwnHome
              ? `linear-gradient(145deg, ${slot.tint}55, ${slot.tint}22 60%, rgba(30,24,14,0.9))`
              : "linear-gradient(145deg, rgba(70,56,28,0.7), rgba(30,24,14,0.85))"),
        border: filled ? `1px solid ${SD.goldBright}` : `1px solid ${slot.tint}66`,
        boxShadow: filled
          ? `0 0 ${8 + pulse * 10}px ${glowC}77, 0 3px 8px rgba(0,0,0,0.6), inset 0 1px 0 ${SD.gold}44`
          : (isOwnHome
              ? `0 0 ${4 + pulse * 6}px ${slot.tint}44, 0 2px 6px rgba(0,0,0,0.5)`
              : "0 2px 6px rgba(0,0,0,0.5)"),
        boxSizing: "border-box",
      }}>
        {/* the canvas within the frame */}
        <div style={{
          width: "100%", height: "100%",
          borderRadius: 1,
          overflow: "hidden",
          background: filled
            ? "rgba(4,3,12,0.9)"
            : "linear-gradient(180deg, rgba(14,11,26,0.9), rgba(8,6,18,0.95))",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative",
        }}>
          {filled ? (
            piece.image ? (
              <img
                src={piece.image}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div style={{ fontSize: h * 0.4, filter: `drop-shadow(0 0 6px ${glowC})` }}>
                {prov === PIECE_PROVENANCE.earned ? "♥" : "◆"}
              </div>
            )
          ) : (
            // empty frame interior — a faint hanging hint in your own hall
            <div style={{
              fontSize: h * 0.34,
              color: isOwnHome ? `${slot.tint}` : "#2a2540",
              opacity: isOwnHome ? 0.35 + pulse * 0.25 : 0.4,
            }}>{isOwnHome ? "+" : "◻"}</div>
          )}
          {/* glass sheen */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 45%)",
            pointerEvents: "none",
          }} />
        </div>
      </div>
    </div>
  );
}

// The whole south-wall gallery band. Renders every frame slot; a slot with a
// hung pieceId that resolves to an owned+hydrated piece shows the art.
function HallFrames({ decorMap, galleryById, isOwnHome, tile, frame }) {
  return (
    <>
      {FRAME_SLOTS.map((slot) => {
        const pieceId = decorMap[slot.slotId];
        const piece = pieceId ? galleryById[pieceId] : null;   // may be null if listed/gone
        return (
          <FrameVisual
            key={slot.slotId}
            slot={slot}
            piece={piece}
            isOwnHome={isOwnHome}
            tile={tile}
            frame={frame}
          />
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// DEED BOOK PEDESTAL VISUAL — the lectern that opens the full catalog.
// ─────────────────────────────────────────────────────────────
function DeedBookVisual({ tile, frame }) {
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.05);
  return (
    <div style={{
      position: "absolute",
      left: DEED_BOOK.x * tile, top: (DEED_BOOK.y - 0.6) * tile,
      width: tile, height: tile * 1.4,
      zIndex: 5, pointerEvents: "none",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{
        fontSize: tile * 0.7,
        filter: `drop-shadow(0 0 ${8 + pulse * 12}px ${SD.gold})`,
        color: SD.gold,
      }}>📖</div>
      <div style={{
        width: tile * 0.6, height: tile * 0.5,
        background: `linear-gradient(180deg, ${SD.gold}22, rgba(8,4,20,0.95))`,
        border: `1px solid ${SD.gold}66`,
        borderRadius: "3px 3px 5px 5px",
        marginTop: -2,
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TROPHY INSPECT CARD — appears when you inspect a placed deed.
// ─────────────────────────────────────────────────────────────
function TrophyInspect({ trophy, earned, record, onClose }) {
  const def = ACHIEVEMENTS[trophy.id];
  const display = getAchievementDisplay(trophy.id, earned);
  const rarity = ACHIEVEMENT_RARITY_META[def.rarity] || ACHIEVEMENT_RARITY_META.common;
  const gameLabel = GAME_NAMESPACES[def.game]?.label || def.game;
  const when = record?.earnedAt
    ? new Date(record.earnedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace" || e.key === "q" || e.key === "Q") {
        e.preventDefault(); onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(0,0,0,0.78)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "rgba(5,3,20,0.98)",
        border: `1px solid ${earned ? rarity.tint : SD.void4}`,
        borderRadius: 16, padding: "28px 34px",
        width: "min(400px, 90vw)", textAlign: "center",
        boxShadow: earned ? `0 0 40px ${rarity.tint}44` : "0 0 24px rgba(0,0,0,0.6)",
      }}>
        <div style={{
          fontSize: 64, marginBottom: 10,
          filter: earned ? `drop-shadow(0 0 16px ${rarity.tint})` : "grayscale(0.9)",
          opacity: earned ? 1 : 0.4,
        }}>{display.masked ? "✶" : display.icon}</div>

        <div style={{
          fontSize: 9, letterSpacing: 3, fontFamily: SD.fontDisplay,
          color: trophy.tint, textShadow: `0 0 8px ${trophy.tint}`, marginBottom: 8,
        }}>{gameLabel.toUpperCase()} · {rarity.label.toUpperCase()}</div>

        <div style={{
          fontSize: 18, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: earned ? SD.starWhite : SD.starDim, marginBottom: 8,
        }}>{display.name}</div>

        <div style={{
          fontSize: 12, color: SD.starDim, fontFamily: SD.fontDisplay,
          lineHeight: 1.6, marginBottom: 16,
        }}>{display.desc}</div>

        <div style={{
          fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
          color: earned ? SD.goldBright : "#5a5470",
          padding: "8px 0", borderTop: `1px solid ${SD.void4}`,
        }}>
          {earned
            ? (when ? `♥ CLAIMED ${when.toUpperCase()}` : "♥ CLAIMED")
            : `🔒 ${def.points} RENOWN · UNCLAIMED`}
        </div>

        <button onClick={onClose} style={{
          width: "100%", padding: "10px", marginTop: 14,
          background: "transparent", border: `1px solid ${SD.void4}`,
          borderRadius: 8, cursor: "pointer",
          color: SD.starDim, fontSize: 10,
          fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}>← CLOSE</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HALL DECOR HOOK — the read side of M1 Frames.
// Subscribes to the owner's decor node and the owner's gallery, joins each
// owned piece to its mint record (for the actual image/title/creator), and
// returns everything the frame layer needs. READ-ONLY on the gallery and the
// mints; the only thing this hook ever WRITES is the decor node — and only
// when it's your OWN hall (a visitor can look, never rearrange).
//
//   decorMap:  { [slotId]: pieceId }          — what hangs where
//   galleryById: { [pieceId]: hydratedPiece } — your framable art, hydrated
//   hangPiece(slotId, pieceId) / takeDown(slotId) — owner-only writers
// ─────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────
// ⭐ OW-24 · FURNISHING THE INSIDE
// ───────────────────────────────────────────────────────────
// caP'n, 2026-09-13, on furnishing the Hall: "beautiful <3".
//
// ⛑ WHAT WAS ALREADY BUILT AND DID NOT NEED TOUCHING: props carry an
// `area`, catalogue items declare which `areas` they are allowed in, and
// `placeProp` has honoured `AREAS.HALL` since the day it was written.
// The only thing missing was that this canvas drew none of it. So this
// section is a RENDERER and an INPUT SURFACE. It adds no schema, no
// node, no rules work and no second catalogue.
//
// ⚠ THE ONE ENGINE BUG IT SURFACED, fixed in `minerium.js` the same
// session: `collides()` swept every prop in the holding regardless of
// area, and the Hall and the yard are two separate tile spaces that both
// start at (0,0). A chair at hall (3,4) collided with a crop bed at yard
// (3,4) — refusing a placement in a room that visibly has nothing in it.
// Invisible until a second room existed, which is why it is called out
// here rather than quietly patched.
// ───────────────────────────────────────────────────────────

// The holding, read the same instanced way the frames are: the OWNER's
// node, whoever is standing in it. A visitor sees the room furnished
// exactly as its owner left it and can write none of it.
function useHallFurnishings(ownerUid, viewerUid, isOwnHome) {
  const [holding, setHolding] = useState(emptyHolding);
  const [stock, setStock] = useState({});

  useEffect(() => {
    if (!ownerUid) return undefined;
    return listenToHolding(ownerUid, setHolding);
  }, [ownerUid]);

  // ⚠ THE SHED IS THE VIEWER'S, NOT THE OWNER'S, and it is only ever
  // read when they are the same person. A guest's own shed has no
  // business being legible inside somebody else's house, and the owner's
  // shed is not a guest's to see.
  useEffect(() => {
    if (!isOwnHome || !viewerUid) { setStock({}); return undefined; }
    return listenToStock(viewerUid, setStock);
  }, [isOwnHome, viewerUid]);
  // the worn things you own — skies out there, floors and walls in here
  const [owned, setOwned] = useState({});
  useEffect(() => {
    if (!isOwnHome || !viewerUid) { setOwned({}); return undefined; }
    return listenToOwned(viewerUid, setOwned);
  }, [isOwnHome, viewerUid]);

  // ⭐ FOUND FROM EITHER DOOR (2026-09-15). Pier → Hall is the first
  // visit, and the founding crate only landed when the YARD mounted — so
  // the first time a player opened FURNISH in here the shed said "nothing
  // belongs indoors yet" and pointed at a counter outside. Same one-shot
  // grant, same in-flight dedupe; inert forever after.
  const [founded, setFounded] = useState(null);
  useEffect(() => {
    if (!isOwnHome || !viewerUid) return undefined;
    let alive = true;
    foundHolding(viewerUid).then((r) => { if (alive && r?.ok) setFounded(r); });
    return () => { alive = false; };
  }, [isOwnHome, viewerUid]);

  // Name a missing 2D recipe at boot rather than letting a player find a
  // blank patch of floor where the thing they paid for should be.
  useEffect(() => { auditProp2D(); }, []);

  const hallProps = useMemo(
    () => propsInArea(holding.props, AREAS.HALL),
    [holding.props]
  );

  return { holding, stock, owned, hallProps, founded };
}

// ───────────────────────────────────────────────────────────
// PLACED DECOR — the furniture layer, drawn between the floor and the
// player. Purely presentational; it writes nothing and knows nobody.
//
// ⛑ STACKS RESOLVE THROUGH THE ENGINE. A prop sitting ON another does
// not store its own position — `resolvePlacement()` walks up the stack.
// Reading `p.x` directly here would draw every stacked piece at (0,0),
// which is exactly the bug caP'n's "a torch inside a stuffed animal"
// toy would hit on its first use.
// ───────────────────────────────────────────────────────────
// ⛑ THE REACH IS THE ENGINE'S. `facingTile` lived here (and a second
// copy lived in Hall3D's ghost, despite the "written once" note). Both
// are `reachTile(AREAS.HALL, pos, facing, footprint)` in minerium.js now
// — the same function the yard uses — so the two rooms cannot disagree
// about where a piece lands. (2026-09-15)
const facingTile = (pos, facing, fp = 1) => reachTile(AREAS.HALL, pos, facing, fp);

function PlacedDecor({ props: hallProps, allProps, tile, frame, selectedId }) {
  if (!hallProps?.length) return null;
  return (
    <>
      {hallProps.map((p) => {
        const at = resolvePlacement(allProps, p.propId) || p;
        const item = ITEMS[p.itemId];
        if (!item) return null;
        return (
          <div key={p.propId} style={{
            position: "absolute",
            left: at.x * tile, top: at.y * tile,
            width: tile, height: tile,
            // ⚠⚠ AN INTEGER. THIS IS NOT A STYLE NOTE.
            // It read `4 + Math.min(1, at.y / ROWS)`, which for every
            // walkable row produced 4.2…4.8 — and CSS `z-index` takes an
            // <integer>, so a fractional value is INVALID and the whole
            // declaration is dropped. Every piece of furniture fell back
            // to `z-index: auto`, which paints BELOW any positioned
            // element with a positive z-index — including
            // `HallRoomCanvas` at 1, which fills the floor opaquely.
            // Net effect: the entire OW-24 feature was invisible. You
            // bought a chair, placed it, read "Wooden chair placed." and
            // saw bare floorboards, because it was under them.
            // ⛑ A dropped CSS declaration is silent — no console warning,
            // no React error, and it PARSES AND TYPE-CHECKS PERFECTLY.
            // Found by adversarial review 2026-09-13.
            //
            // 4 is the furniture band: above the room canvas (1) and the
            // sconces/lights (2), below the player (9). +1 for the far
            // half of the room so a chair by the south wall overlaps one
            // by the north wall, and +1 again for a stacked piece so it
            // draws in front of the thing it is balanced on.
            zIndex: 4 + (at.y >= FLOOR_MID ? 1 : 0) + (p.on ? 1 : 0),
            pointerEvents: "none",
          }}>
            <MineriumProp2D
              itemId={p.itemId}
              tile={tile}
              frame={frame}
              rot={at.rot || 0}
              lift={at.lift || 0}
              sel={selectedId === p.propId}
            />
          </div>
        );
      })}
    </>
  );
}

// ───────────────────────────────────────────────────────────
// THE GHOST — the piece you are holding, shown on the tile you face.
// ───────────────────────────────────────────────────────────
function DecorGhost({ itemId, at, tile, frame, rot = 0, blocked }) {
  if (!itemId || !at) return null;
  return (
    <div style={{
      position: "absolute",
      left: at.x * tile, top: at.y * tile,
      width: tile, height: tile,
      // ⚠ 8, not 9. PlayerEntity draws its body at 9 and its companion at
      // 10; a ghost level with them flickers in front of whichever
      // rendered last. It is a preview, so it loses every tie.
      zIndex: 8, pointerEvents: "none",
    }}>
      <div style={{
        position: "absolute", inset: tile * 0.06,
        border: `1px dashed ${blocked ? "#e8453c" : "#9ad46a"}`,
        borderRadius: 4,
        background: blocked ? "rgba(232,69,60,0.10)" : "rgba(154,212,106,0.08)",
      }} />
      <MineriumProp2D itemId={itemId} tile={tile} frame={frame} rot={rot} ghost blocked={blocked} />
    </div>
  );
}

// (HallShed — the indoor shed panel — retired 2026-09-15 in favour of the
// shared `ShedPanel` in buildKit.jsx, so both rooms have one shed.)

function useHallDecor(ownerUid, isOwnHome) {
  const [decorMap, setDecorMap]     = useState({});
  const [galleryRaw, setGalleryRaw] = useState([]);   // raw pieces (pieceId, artId, …)
  const [mints, setMints]           = useState({});   // artId → mint record (cached)

  // 1) Decor: what hangs in which frame. Instanced by ownerUid, same as
  //    presence — a visitor reads YOUR decor, everyone sees the same walls.
  useEffect(() => {
    if (!ownerUid) { setDecorMap({}); return; }
    const dRef = ref(db, `homes/${ownerUid}/decor`);
    const unsub = onValue(dRef, (snap) => setDecorMap(snap.val() || {}));
    return () => unsub();
  }, [ownerUid]);

  // 2) The owner's gallery (raw). This is the pool of framable art — pieces
  //    the owner OWNS. A listed (escrowed) piece leaves the gallery, so it
  //    drops out of here automatically and its frame goes empty. No coupling.
  useEffect(() => {
    if (!ownerUid) { setGalleryRaw([]); return; }
    const unsub = listenToGallery(ownerUid, (pieces) => setGalleryRaw(pieces || []));
    return () => unsub && unsub();
  }, [ownerUid]);

  // 3) Hydrate: fetch each referenced mint once and cache it. We only need
  //    mints for artIds that are actually owned or hung — small set, fetched
  //    lazily, never re-fetched.
  useEffect(() => {
    const wanted = new Set();
    for (const p of galleryRaw) if (p.artId) wanted.add(p.artId);
    // also any hung piece whose artId we can't see in gallery (shouldn't
    // happen — you can't hang what you don't own — but keeps the join total)
    let alive = true;
    const missing = [...wanted].filter((artId) => mints[artId] === undefined);
    if (missing.length === 0) return;
    (async () => {
      const fetched = {};
      for (const artId of missing) {
        try { fetched[artId] = await getMint(artId); }
        catch { fetched[artId] = null; }
      }
      if (alive && Object.keys(fetched).length) {
        setMints((m) => ({ ...m, ...fetched }));
      }
    })();
    return () => { alive = false; };
  }, [galleryRaw, mints]);

  // Hydrated, framable pieces keyed by pieceId (image/title joined from mint).
  const galleryById = useMemo(() => {
    const out = {};
    for (const p of galleryRaw) {
      const mint = p.artId ? mints[p.artId] : null;
      out[p.pieceId] = {
        ...p,
        title: mint?.title || (p.provenance === "earned" ? "Earned Deed" : "Unknown Work"),
        image: mint?.image || "",
        creatorName: mint?.creatorName || "—",
        editionSize: mint?.editionSize || null,
        prov: PIECE_PROVENANCE[p.provenance] || PIECE_PROVENANCE.bazaar,
      };
    }
    return out;
  }, [galleryRaw, mints]);

  // Which pieceIds are already hung (so the picker can gray them out and we
  // never hang the same piece in two frames at once).
  const hungPieceIds = useMemo(
    () => new Set(Object.values(decorMap).filter(Boolean)),
    [decorMap]
  );

  // Owner-only writers. Guarded on isOwnHome so a visitor physically cannot
  // write to someone else's decor node from this hook.
  const hangPiece = useCallback((slotId, pieceId) => {
    if (!isOwnHome || !ownerUid || !FRAME_SLOT_BY_ID[slotId]) return;
    set(ref(db, `homes/${ownerUid}/decor/${slotId}`), pieceId);
  }, [isOwnHome, ownerUid]);

  const takeDown = useCallback((slotId) => {
    if (!isOwnHome || !ownerUid) return;
    remove(ref(db, `homes/${ownerUid}/decor/${slotId}`));
  }, [isOwnHome, ownerUid]);

  return { decorMap, galleryById, hungPieceIds, hangPiece, takeDown };
}

// ─────────────────────────────────────────────────────────────
// FRAME PICKER — owner-only. Opens when you activate an EMPTY frame in your
// own hall. Shows your framable gallery (hydrated art), grays out pieces
// already hung elsewhere, and hangs the chosen piece into this slot.
// ─────────────────────────────────────────────────────────────
function FramePicker({ slot, galleryById, hungPieceIds, onHang, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "q" || e.key === "Q") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pieces = Object.values(galleryById);
  const wingTint = slot.tint;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 210,
      background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "rgba(5,3,20,0.98)",
        border: `1px solid ${wingTint}88`,
        borderRadius: 16, padding: "22px 24px",
        width: "min(520px, 92vw)", maxHeight: "82vh",
        display: "flex", flexDirection: "column",
        boxShadow: `0 0 40px ${wingTint}44`,
      }}>
        <div style={{
          fontSize: 9, letterSpacing: 3, fontFamily: SD.fontDisplay,
          color: wingTint, textShadow: `0 0 8px ${wingTint}`, marginBottom: 4, textAlign: "center",
        }}>HANG ART IN THIS FRAME</div>
        <div style={{
          fontSize: 15, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: SD.starWhite, marginBottom: 14, textAlign: "center",
        }}>Your Gallery</div>

        {pieces.length === 0 ? (
          <div style={{
            fontSize: 12, color: SD.starDim, fontFamily: SD.fontDisplay,
            lineHeight: 1.7, textAlign: "center", padding: "24px 8px",
          }}>
            No art to hang yet. Mint or buy a piece at the Boardwalk Bazaar,
            and it'll appear here to frame.
          </div>
        ) : (
          <div style={{
            overflowY: "auto", display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 12, paddingRight: 4,
          }}>
            {pieces.map((p) => {
              const alreadyHung = hungPieceIds.has(p.pieceId);
              return (
                <button
                  key={p.pieceId}
                  disabled={alreadyHung}
                  onClick={() => { if (!alreadyHung) onHang(p.pieceId); }}
                  style={{
                    background: "rgba(8,5,22,0.9)",
                    border: `1px solid ${alreadyHung ? SD.void4 : (p.prov?.color || SD.gold) + "88"}`,
                    borderRadius: 10, padding: 8, cursor: alreadyHung ? "not-allowed" : "pointer",
                    opacity: alreadyHung ? 0.4 : 1,
                    display: "flex", flexDirection: "column", gap: 6, textAlign: "left",
                  }}
                >
                  <div style={{
                    width: "100%", aspectRatio: "1 / 1", borderRadius: 6, overflow: "hidden",
                    background: "rgba(4,3,12,0.9)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: alreadyHung ? "none" : `0 0 10px ${(p.prov?.color || SD.gold)}44`,
                  }}>
                    {p.image
                      ? <img src={p.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ fontSize: 30 }}>{p.prov === PIECE_PROVENANCE.earned ? "♥" : "◆"}</div>}
                  </div>
                  <div style={{
                    fontSize: 10, fontFamily: SD.fontDisplay, fontWeight: 900,
                    color: SD.starWhite, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{p.title}</div>
                  <div style={{ fontSize: 8, letterSpacing: 1, color: p.prov?.color || SD.starDim, fontFamily: SD.fontDisplay }}>
                    {alreadyHung ? "ALREADY HUNG" : (p.prov?.label?.split("—")[0]?.trim().toUpperCase() || "OWNED")}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <button onClick={onClose} style={{
          width: "100%", padding: "10px", marginTop: 16,
          background: "transparent", border: `1px solid ${SD.void4}`,
          borderRadius: 8, cursor: "pointer",
          color: SD.starDim, fontSize: 10, fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}>← CLOSE</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FRAME INSPECT — the plaque beside a hung piece. Shows the art, its Sigil
// Code, and its provenance radiance. In your OWN hall, a "Take Down" button
// returns the piece to the wall-of-frames pool (the piece never leaves your
// gallery — taking down just clears the slot).
// ─────────────────────────────────────────────────────────────
function FrameInspect({ piece, isOwnHome, onTakeDown, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace" || e.key === "q" || e.key === "Q") {
        e.preventDefault(); onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const prov = piece?.prov || PIECE_PROVENANCE.bazaar;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "rgba(5,3,20,0.98)",
        border: `1px solid ${prov.color}`,
        borderRadius: 16, padding: "24px 28px",
        width: "min(420px, 92vw)", textAlign: "center",
        boxShadow: `0 0 40px ${prov.color}44`,
      }}>
        {/* the piece */}
        <div style={{
          width: "100%", aspectRatio: "4 / 3", borderRadius: 8, overflow: "hidden",
          background: "rgba(4,3,12,0.9)", marginBottom: 14,
          border: `2px solid ${SD.gold}88`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 0 18px ${prov.color}55`,
        }}>
          {piece.image
            ? <img src={piece.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ fontSize: 72, filter: `drop-shadow(0 0 16px ${prov.color})` }}>{prov === PIECE_PROVENANCE.earned ? "♥" : "◆"}</div>}
        </div>

        <div style={{
          fontSize: 9, letterSpacing: 3, fontFamily: SD.fontDisplay,
          color: prov.color, textShadow: `0 0 8px ${prov.color}`, marginBottom: 8,
        }}>{prov.label.toUpperCase()}</div>

        <div style={{
          fontSize: 18, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: SD.starWhite, marginBottom: 6,
        }}>{piece.title}</div>

        <div style={{ fontSize: 11, color: SD.starDim, fontFamily: SD.fontDisplay, marginBottom: 12 }}>
          by {piece.creatorName}
        </div>

        <div style={{
          fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          color: SD.goldBright, padding: "8px 0", borderTop: `1px solid ${SD.void4}`,
          wordBreak: "break-all",
        }}>{piece.code}</div>

        {isOwnHome && (
          <button onClick={onTakeDown} style={{
            width: "100%", padding: "10px", marginTop: 14,
            background: "transparent", border: `1px solid ${SD.gold}66`,
            borderRadius: 8, cursor: "pointer",
            color: SD.gold, fontSize: 10, fontFamily: SD.fontDisplay, letterSpacing: 2,
          }}>↧ TAKE DOWN</button>
        )}
        <button onClick={onClose} style={{
          width: "100%", padding: "10px", marginTop: 8,
          background: "transparent", border: `1px solid ${SD.void4}`,
          borderRadius: 8, cursor: "pointer",
          color: SD.starDim, fontSize: 10, fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}>← CLOSE</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN — PIER HOME
// ─────────────────────────────────────────────────────────────
export default function PierHome({
  ownerUid,
  ownerName = "",
  viewerUid,
  viewerName,
  profile,
  onLeave,
  // ⭐ THE DOOR OUT. The Hall is the inside of a holding; the Minerium is
  // the rest of it. Optional so this component still mounts anywhere it
  // mounted before — an absent handler simply means no door, not a crash.
  onEnterMinerium,
}) {
  const isOwnHome = ownerUid === viewerUid;

  const [tile, setTile]   = useState(calcTile);
  const [pos, setPos]     = useState(SPAWN);
  const [facing, setFacing] = useState({ x: 0, y: 1 });
  const [camOffset, setCamOffset] = useState({ x: 0, y: 0 });
  const [otherPlayers, setOtherPlayers] = useState({});

  const [nearTrophy, setNearTrophy]   = useState(null);
  const [nearBook, setNearBook]       = useState(false);
  const [nearFrame, setNearFrame]     = useState(null);   // frame slot you're facing
  const [nearYardDoor, setNearYardDoor] = useState(false); // the shingle → the Minerium
  const [inspect, setInspect]         = useState(null);   // trophy being inspected
  const [showBook, setShowBook]       = useState(false);  // full catalog overlay
  const [pickerSlot, setPickerSlot]   = useState(null);   // empty frame → hang picker
  const [frameInspect, setFrameInspect] = useState(null); // hung piece → inspect card

  // M1 — Frames in the Hall. Read-only view into the owner's gallery.
  const { decorMap, galleryById, hungPieceIds, hangPiece, takeDown } =
    useHallDecor(ownerUid, isOwnHome);

  // ⭐ OW-24 — Furnishings in the Hall. The owner's holding, the viewer's
  // shed, and the same engine the yard runs on.
  const { holding, stock, owned, hallProps, founded } = useHallFurnishings(ownerUid, viewerUid, isOwnHome);
  const [furnishing, setFurnishing] = useState(false);   // the shed panel is open
  const [heldItem, setHeldItem]     = useState(null);    // the piece on your shoulder
  const [heldRot, setHeldRot]       = useState(0);
  // ⭐ OW-42 · the Architect's tool and the deed it carries (see THE ARCHITECT)
  const [archTool, setArchTool]     = useState(null);
  const [heldDeed, setHeldDeed]     = useState(null);
  const [nearDecor, setNearDecor]   = useState(null);    // propId you are facing
  // ⭐ (2026-09-15) the yard's build tooling, indoors: a piece you CLICKED,
  // a piece you are MOVING, a tile you TAPPED to aim at, undo, bursts.
  const [pickedDecor, setPickedDecor] = useState(null);
  const [movingPropId, setMoving]   = useState(null);
  const movingRef = useRef(null); movingRef.current = movingPropId;
  const [aim, setAim]               = useState(null);
  const aimRef = useRef(null); aimRef.current = aim;
  const undo = useUndo();
  const { bursts, burst } = useBursts();
  const [decorBusy, setDecorBusy]   = useState(false);
  const [decorFlash, setDecorFlash] = useState(null);
  const anyModalRef = useRef(false);

  const decorSay = useCallback((ok, text) => {
    setDecorFlash({ ok, text });
    window.clearTimeout(decorSay._t);
    decorSay._t = window.setTimeout(() => setDecorFlash(null), 3400);
    if (!ok) sfx.deny();
  }, []);
  // the founding, if it happened just now, is worth a line
  useEffect(() => {
    if (founded?.ok) decorSay(true, `W'ily left a founding crate in your shed — open ⚒ FURNISH.`);
  }, [founded, decorSay]);

  // ⚠ OW-24. A grower's ripeness is measured against the SERVER's clock,
  // never the browser's — the yard binds this on mount and the Hall now
  // reads the same timers, so it has to bind it too. A local clock that
  // is three minutes fast pays out three minutes of Dawgs that were
  // never grown.
  useEffect(() => bindServerClock(), []);
  // ⚠ A yaw left over from the 3D rig would silently rotate input in the 2D
  // view (or on the pier), and nothing would report it. Reset on unmount.
  useEffect(() => () => resetGroundForward(), []);

  const [frame, setFrame] = useState(0);
  useEffect(() => {
    ensureHallKeyframes();
    // ⚑ In 3D nothing animates off `frame` — only `decorAction`'s ripeness
    // re-check reads it — so it ticks once a second instead of 16 times,
    // and PierHome stops re-rendering itself at 16Hz behind the canvas.
    const t = setInterval(() => setFrame((f) => f + 1), is3D ? 1000 : 60);
    return () => clearInterval(t);
  }, []);
  const floorPulse = 0.5 + 0.5 * Math.sin(frame * 0.04);

  // Owner's deeds — the trophies. Read live, cross-game, read-only.
  const { map: deedMap } = useAchievements(ownerUid);
  // ⚠ MEMOISED — Hall3D is React.memo'd and repaints its deed atlas when
  // this changes, which should be when a deed is earned and never otherwise.
  // …and keyed on the SET, not the snapshot: any write under the achievements
  // node hands back a fresh map, and a fresh identity here rebuilt every deed's
  // geometry for an unchanged set (code review).
  const earnedKey = useMemo(() => Object.keys(deedMap || {}).filter((id) => !!deedMap[id]).sort().join(","), [deedMap]);
  const earned3D = useMemo(() => {
    const ids = new Set(earnedKey ? earnedKey.split(",") : []);
    return { key: earnedKey, has: (id) => ids.has(id) };
  }, [earnedKey]);

  const gender      = profile?.gender || "f";
  const companionId = profile?.companionId || "default";

  const keysRef = useRef({});
  const moveRef = useRef(null);
  const posRef  = useRef(pos);
  posRef.current = pos;
  // ⚠ OW-24. The movement loop is rebuilt only when a modal opens, so it
  // must not read `facing` or `holding` out of its closure — both change
  // every few frames and the loop would keep answering with whatever was
  // true the last time a menu closed. Mirrors, written every render.
  const facingRef = useRef(facing);
  facingRef.current = facing;
  const holdingRef = useRef(holding);
  holdingRef.current = holding;
  const axisRef = useRef(null);      // the tile axis last walked (the 3D tie-break)
  // ⭐ FOR THE 3D HALL. Where the others ARE travels by ref (read in
  // useFrame); who is here travels as a key that changes only on arrival or
  // departure. The layout is built once. All three keep Hall3D's memo honest.
  const otherPlayersRef = useRef(otherPlayers);
  otherPlayersRef.current = otherPlayers;
  // name and gender ride in the key: a presence node that arrives bare and
  // fills in a beat later would otherwise keep a nameless, wrong-bodied visitor
  const rosterKey = useMemo(() => Object.entries(otherPlayers)
    .map(([uid, d]) => `${uid}:${d?.name || ""}:${d?.gender || ""}`).sort().join(","), [otherPlayers]);
  // ⭐ OW-42 · the Hall on screen is the OWNER's interior. Composed by key,
  // so a snapshot that changes nothing hands back the same Hall (and the
  // same layout object — Hall3D stays memoised).
  // ⚠ setHallInterior is idempotent (same interior → same Hall), which is
  // what makes calling it here safe; the Hall is put back to plain when the
  // screen goes, so the next one never starts in somebody else's Hall.
  const hallNow = useMemo(() => setHallInterior(holding.interiorRaw), [holding.interiorRaw]);
  useEffect(() => () => { setHallInterior(null); }, []);
  const hallLayout = useMemo(() => getHallLayout(hallNow), [hallNow]);

  const { walkFrame, isIdle, idleFrame } = usePlayerAnimation(pos);

  // ── Resize ────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setTile(calcTile());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    if (is3D) return;          // the 3D rig frames itself; no re-render per step for it
    setCamOffset(calcCamOffset(pos.x, pos.y, tile, COLS, ROWS));
  }, [pos, tile]);

  // ── Presence — INSTANCED by ownerUid ──────────────────────
  // homes/{ownerUid}/players/{viewerUid}. Everyone in the same instance
  // (same ownerUid) sees each other; different instances never mix.
  useEffect(() => {
    if (!ownerUid || !viewerUid || !viewerName) return;
    const basePath = `homes/${ownerUid}/players`;
    const myRef = ref(db, `${basePath}/${viewerUid}`);

    set(myRef, {
      name: viewerName, x: SPAWN.x, y: SPAWN.y,
      facingX: 0, facingY: 1, gender, companionId, ts: Date.now(),
    });

    const unsub = onValue(ref(db, basePath), (snap) => {
      const all = snap.val() || {};
      const cutoff = Date.now() - GHOST_TTL;
      const others = {};
      Object.entries(all).forEach(([uid, data]) => {
        if (uid === viewerUid) return;
        if (!data.ts || data.ts < cutoff) {
          remove(ref(db, `${basePath}/${uid}`));
          return;
        }
        if (data.room === "yard") return;    // they are outside, in the Minerium (2026-09-15)
        others[uid] = data;
      });
      const capped = {};
      Object.entries(others).slice(0, HOME_CAP - 1).forEach(([u, d]) => { capped[u] = d; });
      setOtherPlayers(capped);
    });

    const heartbeat = setInterval(() => {
      set(ref(db, `${basePath}/${viewerUid}/ts`), Date.now());
    }, HEARTBEAT_MS);

    return () => {
      unsub();
      clearInterval(heartbeat);
      remove(myRef);
    };
  }, [ownerUid, viewerUid, viewerName, gender, companionId]);

  const lastSync = useRef(0);
  const syncPos = useCallback((x, y, fx, fy) => {
    if (!ownerUid || !viewerUid) return;
    const now = Date.now();
    if (now - lastSync.current < SYNC_MS) return;
    lastSync.current = now;
    update(ref(db, `homes/${ownerUid}/players/${viewerUid}`), {
      name: viewerName, x, y, facingX: fx, facingY: fy,
      gender, companionId, ts: now,
    });
  }, [ownerUid, viewerUid, viewerName, gender, companionId]);

  // ── Keyboard ──────────────────────────────────────────────
  // ⚠ never while typing (the social dock's friend-code box is live in
  // here). ⭐ A TAP IS A STEP (2026-09-15): the loop samples every 150 ms,
  // so a keypress shorter than the gap was dropped and a one-tile nudge
  // was a coin flip. A movement keydown is remembered until the loop
  // consumes it, so one tap is always one tile.
  const tapStepRef = useRef(null);
  useEffect(() => {
    const MOVE_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "a", "d", "w", "s", "A", "D", "W", "S"]);
    const onDown = (e) => { if (isTypingTarget(e) || anyModalRef.current) return; keysRef.current[e.key] = true; if (MOVE_KEYS.has(e.key) && !e.repeat) tapStepRef.current = e.key; };
    const onUp   = (e) => { keysRef.current[e.key] = false; };
    const clear  = () => { keysRef.current = {}; tapStepRef.current = null; };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", clear);
    };
  }, []);

  // Trophy interactables: a lightweight list the proximity system can scan.
  // For paintings (on the wall row) the "stand tile" is one row below, so
  // you inspect them from the floor in front of the wall.
  const TROPHY_INTERACTABLES = useMemo(
    () => hallNow.trophies.map((t) => ({
      ...t,
      trophyY: t.y,        // keep the true trophy row for rendering the bubble
      y: standTileY(t),    // the tile you actually stand on to inspect
      rx: 0, ry: 0,
    })),
    [hallNow]
  );
  // ⚠ the walk loop is set up once; it reads the deeds through this ref, or a
  // deed moved after it started would still be inspected at its old tile
  const trophiesRef = useRef(TROPHY_INTERACTABLES);
  trophiesRef.current = TROPHY_INTERACTABLES;

  // Frame interactables — you stand on the floor tile ABOVE each south-wall
  // frame to hang/inspect it (same "stand in front of the wall" pattern as
  // north-wall paintings). We keep the true frame row for bubble placement.
  const FRAME_INTERACTABLES = useMemo(
    () => FRAME_SLOTS.map((s) => ({
      ...s,
      id: `frame_${s.slotId}`,
      frameY: s.y,               // true wall row (for the bubble)
      y: frameStandTileY(),      // the floor tile you actually stand on
      rx: 0, ry: 0,
    })),
    []
  );

  // ── Movement + proximity ──────────────────────────────────
  // THE SOCIAL LAYER'S SUPPRESSION SIGNAL — same contract as the Pier's
  // (UnstablePierre.js). A home is a walkable screen with its own
  // full-screen modals, and the friends pill is mounted in index.js
  // where it cannot see any of them. Same predicate as the movement
  // lock: if this screen is not taking WASD, it is not taking a pill.
  useEffect(() => {
    setSocialHidden(!!inspect || showBook || !!pickerSlot || !!frameInspect);
  }, [inspect, showBook, pickerSlot, frameInspect]);
  useEffect(() => () => setSocialHidden(false), []);

  useEffect(() => {
    const menuOpen = !!inspect || showBook || !!pickerSlot || !!frameInspect;
    if (menuOpen) { keysRef.current = {}; tapStepRef.current = null; return; }
    let last = 0;
    const SPEED = 150;
    function loop(ts) {
      if (ts - last > SPEED) {
        last = ts;
        const k = { ...keysRef.current };
        // a tap that came and went between two samples still counts once
        const tap = tapStepRef.current; tapStepRef.current = null;
        if (tap) k[tap] = true;
        let dx = 0, dy = 0;
        if (k["ArrowLeft"]  || k["a"] || k["A"]) dx = -1;
        if (k["ArrowRight"] || k["d"] || k["D"]) dx =  1;
        if (k["ArrowUp"]    || k["w"] || k["W"]) dy = -1;
        if (k["ArrowDown"]  || k["s"] || k["S"]) dy =  1;
        // ⭐ CAMERA-RELATIVE. W means "away from the camera", always — then
        // snapped to the one tile axis it most points along, because this hall
        // walks a tile at a time. In 2D the shared forward is its identity, so
        // this returns the raw keys, and |x| >= |y| keeps the old rule that a
        // held horizontal beats a held vertical. (pierCamera.js)
        if (dx !== 0 || dy !== 0) {
          const r = rotateInput(dx, dy);
          const ax = Math.abs(r.x), ay = Math.abs(r.y);
          let useX = ax >= ay;
          // ⚠ in 3D, near a tie the axis you were already walking wins: at a
          // posed 45° lens, damping noise flipped it every step and one held key
          // walked a staircase (code review). The 2D ties stay exact.
          if (is3D && Math.abs(ax - ay) < 0.12 && axisRef.current) useX = axisRef.current === "x";
          if (is3D) axisRef.current = useX ? "x" : "y";
          if (useX) { dx = Math.sign(r.x); dy = 0; } else { dx = 0; dy = Math.sign(r.y); }
        }
        if (dx !== 0 || dy !== 0) {
          const nx = posRef.current.x + dx;
          const ny = posRef.current.y + dy;
          setFacing({ x: dx, y: dy });
          // ⭐ SOLID FURNITURE (2026-09-15): a pillar is walked around
          const props = holdingRef.current.props;
          const b = blockedByProp(props, AREAS.HALL, nx, ny, 0.3, movingRef.current);
          // a piece you are already inside never blocks the step out of it
          const inside = b && blockedByProp(props, AREAS.HALL, posRef.current.x, posRef.current.y, 0.3, movingRef.current)?.propId === b.propId;
          if (isWalkable(nx, ny) && (!b || inside)) {
            setPos({ x: nx, y: ny });
            syncPos(nx, ny, dx, dy);
            // walking takes the aim back — the ghost follows you again
            if (aimRef.current) { aimRef.current = null; setAim(null); }
          }
        }
        // nearest trophy (interactable footprint = the tile itself, rx/ry 1)
        const t = getNearby(posRef.current, trophiesRef.current);
        const book = getNearby(posRef.current, [DEED_BOOK]);
        const fr = getNearby(posRef.current, FRAME_INTERACTABLES);
        const yard = getNearby(posRef.current, [YARD_DOOR]);
        setNearYardDoor(!!yard);
        setNearBook(!!book && !yard);
        setNearTrophy(!book && !yard && t ? t : null);
        // Frame prompt only when nothing higher-priority is under the cursor.
        setNearFrame(!book && !t && !yard && fr ? fr : null);

        // ⭐ OW-24 — the furnishing on the tile you are FACING. Same tile
        // the ghost lands on, so "put down" and "pick up" are the same
        // reach and a player never has to work out which is which.
        // ⚠ Read through the REF, not `facing`/`holding` from the closure:
        // this loop is re-created only when a modal opens, so anything it
        // closes over goes stale between those points.
        const reach = facingTile(posRef.current, facingRef.current);
        // facing a wall, the hand reaches what hangs on the tile you stand on
        const intoWall = !isWalkable(reach.x, reach.y);
        const hx = intoWall ? posRef.current.x : reach.x, hy = intoWall ? posRef.current.y : reach.y;
        let atTile = null;
        // ⭐ OW-34 · what the hand reaches first when several pieces share
        // the tile: the top of a stack, then furniture, then a hanging, and
        // the rug under all of them last.
        const reachRank = (q) => (q.on ? 3 : { stand: 2, wall: 1, floor: 0 }[layerOf(q.itemId)] ?? 2);
        for (const pr of propsInArea(holdingRef.current.props, AREAS.HALL)) {
          const spot = resolvePlacement(holdingRef.current.props, pr.propId) || pr;
          // ⚠ every tile the piece COVERS, not its pin — a footprint-2
          // piece sits on a tile corner, and Math.round of 6.5 is 7
          if (tilesUnder(spot.x, spot.y, dimsOf(pr.itemId, spot.rot)).some(([tx, ty]) => tx === hx && ty === hy)) {
            if (!atTile || reachRank(pr) > reachRank(atTile)) atTile = pr;
          }
        }
        setNearDecor(atTile ? atTile.propId : null);
      }
      moveRef.current = requestAnimationFrame(loop);
    }
    moveRef.current = requestAnimationFrame(loop);
    return () => { if (moveRef.current) cancelAnimationFrame(moveRef.current); };
  }, [inspect, showBook, pickerSlot, frameInspect, syncPos]);

  const earnedOf = (id) => !!deedMap[id];

  // ── OW-24 · THE GHOST ─────────────────────────────────
  // The piece lands on the tile you are FACING, never the one you are
  // standing on — the same "stand in front of the thing" pattern the
  // trophies, the frames and the shingle all already use, so the Hall
  // keeps exactly one interaction grammar. Placing under your own feet
  // would also mean never seeing what you just put down.
  // the piece under your hand: the one you clicked, else the one you face
  const activeDecor = pickedDecor || nearDecor;
  // a clicked piece stays selected until you walk away from it
  useEffect(() => {
    if (!pickedDecor) return;
    const p = holding.props?.[pickedDecor];
    if (!p) { setPickedDecor(null); return; }
    const at = resolvePlacement(holding.props, pickedDecor) || p;
    if (Math.hypot(at.x - pos.x, at.y - pos.y) > 3.2) setPickedDecor(null);
  }, [pickedDecor, pos, holding.props]);

  // what the ghost is: the held piece, or the piece being moved
  const ghostItem = movingPropId ? holding.props?.[movingPropId]?.itemId : heldItem;
  // ⭐ OW-34 · A HANGING TURNS ITSELF TO THE WALL. Its angle comes from the
  // wall at the tile it is aimed at (the tapped tile, or the one you face);
  // anything else keeps the angle R gave it. Away from any wall it keeps
  // the last good angle and the ghost says why it cannot go there.
  const ghostIsWall = !!ghostItem && layerOf(ghostItem) === LAYER.WALL;
  // ⚠ Standing on the row in front of the wall and facing it, the tile
  // ahead IS the wall — so a hanging goes on the tile you stand on. That is
  // where a player naturally stands to hang something (review).
  const wallHome = useMemo(() => {
    if (!ghostIsWall || aim) return null;
    const t = facingTile(pos, facing, 1);
    if (isWalkable(t.x, t.y)) return null;
    const side = hallWallSideAt(pos.x, pos.y);
    return side ? { x: pos.x, y: pos.y, rot: WALL_SIDE_ROT[side] } : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostIsWall, aim, pos, facing, hallNow]);
  const wallAimRot = useMemo(() => {
    if (!ghostIsWall) return null;
    if (wallHome) return wallHome.rot;
    const t = aim ? { x: aim.x, y: aim.y } : facingTile(pos, facing, 1);
    return wallRotAt(t.x, t.y);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostIsWall, wallHome, aim, pos, facing, hallNow]);
  const ghostRot = ghostIsWall ? (wallAimRot ?? (facing.x ? WALL_SIDE_ROT.W : WALL_SIDE_ROT.N)) : heldRot;
  const ghostDims = useMemo(() => (ghostItem ? dimsOf(ghostItem, ghostRot) : { w: 1, d: 1 }), [ghostItem, ghostRot]);
  const ghostAt = useMemo(() => {
    if (!ghostItem) return null;
    if (aim) {
      // an even extent sits on a tile corner (engine rule), per axis
      const hx = ghostDims.w % 2 === 0 ? 0.5 : 0, hy = ghostDims.d % 2 === 0 ? 0.5 : 0;
      return { x: Math.round(aim.x - hx) + hx, y: Math.round(aim.y - hy) + hy };
    }
    if (wallHome) {
      // a 2-wide hanging from your own tile runs to the right along its wall
      return { x: wallHome.x + (ghostDims.w % 2 === 0 ? 0.5 : 0), y: wallHome.y + (ghostDims.d % 2 === 0 ? 0.5 : 0) };
    }
    return facingTile(pos, facing, ghostDims);
  }, [ghostItem, ghostDims, aim, pos, facing, wallHome]);
  // ⭐ THREE ANSWERS from the engine, with the Hall's floor rule attached
  const ghost = useMemo(() => {
    if (!ghostItem || !ghostAt) return null;
    const g = ghostStateFor(holding.props, AREAS.HALL, ghostAt.x, ghostAt.y, ghostItem, { ignorePropId: movingPropId, floorOk: hallFloorOk, rot: ghostRot });
    if (g.state !== "free") return g;
    const open = hallStaysOpen(holding.props, { propId: movingPropId, itemId: ghostItem, x: ghostAt.x, y: ghostAt.y, rot: ghostRot });
    return open === true ? g : { state: "blocked", on: null, reason: open, sealed: true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostItem, ghostAt, holding.props, movingPropId, ghostRot, hallNow]);
  const ghostAtRef = useRef(ghostAt); ghostAtRef.current = ghostAt;

  // (the old two-question memo — isWalkable + collides — lives in git;
  // `ghostStateFor` with `hallFloorOk` asks both, and says why)
  const ghostBlocked = ghost?.state === "blocked";

  const hallShed = useMemo(() => shedFor(stock, AREAS.HALL), [stock]);

  // ── OW-24 · THE WRITERS ───────────────────────────────
  // ⛑ EVERY ONE OF THEM RETURNS EARLY ON `!isOwnHome`. The engine
  // refuses a non-owner as well (`auth.currentUser?.uid !== uid`), so
  // this is the second of two locks, not the only one — which is what
  // makes OW-23 safe to have opened the yard door to visitors.
  const doPlaceDecor = useCallback(async () => {
    if (!isOwnHome || decorBusy || !ghostItem || !ghostAt || !ghost) return;
    // ⚠ the ghost's answer is re-derived from the engine here, not trusted
    // from the render — a UI guard is not a lock
    let live = ghostStateFor(holding.props, AREAS.HALL, ghostAt.x, ghostAt.y, ghostItem, { ignorePropId: movingPropId, floorOk: hallFloorOk, rot: ghostRot });
    if (live.state === "free") {
      const open = hallStaysOpen(holding.props, { propId: movingPropId, itemId: ghostItem, x: ghostAt.x, y: ghostAt.y, rot: ghostRot });
      if (open !== true) live = { state: "blocked", reason: open };
    }
    if (live.state === "blocked") { decorSay(false, live.reason || "It cannot go there."); return; }
    const item = ITEMS[ghostItem];
    setDecorBusy(true);
    try {
      if (movingPropId) {
        const from = holding.props?.[movingPropId];
        const r = await moveProp(viewerUid, movingPropId, ghostAt.x, ghostAt.y, holding.props, ghostRot, { floorOk: hallFloorOk });
        if (!r.ok) { decorSay(false, r.message || "It would not go there."); return; }
        sfx.place();
        if (from) undo.push({ type: "move", propId: movingPropId, x: from.x, y: from.y, rot: from.rot || 0 });
        setMoving(null); setAim(null); setPickedDecor(movingPropId);
        decorSay(true, "Moved.");
        return;
      }
      const on = live.state === "stack" ? live.on : null;
      const r = await placeProp(viewerUid, {
        itemId: heldItem, area: AREAS.HALL, x: ghostAt.x, y: ghostAt.y, rot: ghostRot, on,
      }, holding.props);
      if (!r.ok) { decorSay(false, r.message || "It would not stand up."); return; }
      on ? sfx.stack() : sfx.place();
      undo.push({ type: "place", propId: r.propId, itemId: heldItem });
      decorSay(true, on ? `${item.name} balanced on the ${live.onName}.` : `${item.name} placed.`);
      // ⛑ KEEP HOLDING IT while you still have more, and step the aim on
      // so the next slab is not red on top of the one you just laid.
      const left = (stock?.[heldItem] || 0) - 1;
      if (left <= 0) { setHeldItem(null); setAim(null); }
      // a hanging steps ALONG its wall, never into it (review)
      else setAim(nextAim(ghostAt, ghostIsWall ? (ghostRot ? { x: 0, y: 1 } : { x: 1, y: 0 }) : facing,
                          holding.props, heldItem, AREAS.HALL, { floorOk: hallFloorOk, rot: ghostRot }));
    } finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, heldItem, ghostItem, ghost, ghostAt, ghostRot, ghostIsWall, holding.props, viewerUid, stock, decorSay, movingPropId, facing, undo]);

  const doPickUpDecor = useCallback(async (propId) => {
    if (!isOwnHome || decorBusy || !propId) return;
    setDecorBusy(true);
    try {
      const r = await pickUpProp(viewerUid, propId, holding.props, { floorOk: hallFloorOk });
      if (r.ok) {
        sfx.pickup();
        undo.push({ type: "pickup", propId, itemId: r.itemId, x: r.x, y: r.y, rot: r.rot, on: r.on || null });
        if (pickedDecor === propId) setPickedDecor(null);
        decorSay(true, r.orphaned
          ? `Back in the shed. ${r.orphaned} thing${r.orphaned > 1 ? "s" : ""} came off the top.`
          : "Back in the shed.");
      } else decorSay(false, r.message || "It would not come up.");
    } finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, viewerUid, holding.props, decorSay, undo, pickedDecor]);

  // ⭐ UNDO — the last place, pick-up or move, through the same writers
  const doUndoDecor = useCallback(async () => {
    if (!isOwnHome || decorBusy || !undo.canUndo) return;
    const e = undo.pop(); if (!e) return;
    setDecorBusy(true);
    try {
      let r = { ok: false };
      const P = holdingRef.current.props;
      if (e.type === "place")  r = await pickUpProp(viewerUid, e.propId, P, { floorOk: hallFloorOk });
      if (e.type === "pickup") {
        // a piece that went back ON something never closes a way
        const open = e.on ? true : hallStaysOpen(P, { itemId: e.itemId, x: e.x, y: e.y, rot: e.rot });
        r = open !== true ? { ok: false, reason: "sealed", message: open }
          : await placeProp(viewerUid, { itemId: e.itemId, area: AREAS.HALL, x: e.x, y: e.y, rot: e.rot, on: e.on || null }, P, { floorOk: hallFloorOk });
        // the piece came back under a NEW id — older steps must follow it
        if (r.ok) undo.remap(e.propId, r.propId);
      }
      if (e.type === "move" || e.type === "turn") {
        const open = P[e.propId]?.on ? true : hallStaysOpen(P, { propId: e.propId, itemId: P[e.propId]?.itemId, x: e.x, y: e.y, rot: e.rot });
        r = open !== true ? { ok: false, reason: "sealed", message: open }
          // a turn is taken back by turning the other way (works stacked, too)
          : e.type === "turn" ? await rotateProp(viewerUid, e.propId, -1, P, { floorOk: hallFloorOk })
          : await moveProp(viewerUid, e.propId, e.x, e.y, P, e.rot, { floorOk: hallFloorOk });
      }
      if (r.ok) { sfx.undo(); decorSay(true, "Undone."); }
      else {
        const kept = undo.restore(e, r);
        decorSay(false, `${r.message || "That could not be undone."}${kept ? " (Z again once it can.)" : ""}`);
      }
    } finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, undo, viewerUid, decorSay]);

  const startMoveDecor = useCallback((propId) => {
    if (!isOwnHome || !propId) return;
    // a stacked piece is lifted off first — a green ghost that E then
    // refuses is the prompt lying (E2E review)
    if (holding.props?.[propId]?.on) { decorSay(false, "Take it off the stack first — PICK UP, then put it down."); return; }
    setHeldItem(null); setAim(null); setArchTool(null); setHeldDeed(null);
    setHeldRot(holding.props?.[propId]?.rot || 0);
    setMoving((cur) => (cur === propId ? null : propId));
    setFurnishing(true);
  }, [isOwnHome, holding.props, decorSay]);

  // the Hall's finishes — floor and walls, worn like a sky
  const doWearFinish = useCallback(async (itemId) => {
    if (!isOwnHome || decorBusy) return;
    setDecorBusy(true);
    try {
      const r = await wearItem(viewerUid, itemId);
      if (r.ok) { sfx.ui(); decorSay(true, `${ITEMS[itemId].name} — done.`); }
      else decorSay(false, r.message || "It would not settle.");
    } finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, viewerUid, decorSay]);

  // ════════════════════════════════════════════════════════════════
  // ⭐ OW-42 · THE ARCHITECT — walls, doorways, paint and deeds, by hand
  // ────────────────────────────────────────────────────────────────
  // A tool in FURNISH instead of a piece: E (or a tap) builds on the tile
  // you face (or tapped); SHIFT-click, or LINE on a phone, draws a straight
  // run from the last tile. Every wall is down while a tool is out, so the
  // plan reads. hallInterior.js decides what is allowed; this only asks.
  // ════════════════════════════════════════════════════════════════
  const [archFinish, setArchFinish] = useState(null);   // null = the Hall's own / bare
  const [archLine, setArchLine]     = useState(false);
  const [archOpen, setArchOpen]     = useState(() => !isMobile());
  const [confirmHome, setConfirmHome] = useState(false);
  const archLast = useRef(null);
  const shiftRef = useRef(false);
  const archUndoRef = useRef([]);
  const [archUndoN, setArchUndoN] = useState(0);
  // ⚠ ONE WRITE AT A TIME, decided by a REF (a state flag is a render late:
  // two quick taps both read "not busy" and both validate the old Hall)
  const archBusy = useRef(false);
  const archOn = isOwnHome && furnishing && !!archTool;
  const interiorNow = useMemo(() => normaliseInterior(holding.interiorRaw), [hallNow]);  // eslint-disable-line react-hooks/exhaustive-deps
  // the interior as the last write left it — the next stroke builds on THIS,
  // not on a snapshot that has not come back yet
  const interiorRef = useRef(interiorNow);
  const seenInterior = useRef(interiorNow);
  if (seenInterior.current !== interiorNow) { seenInterior.current = interiorNow; interiorRef.current = interiorNow; }
  useEffect(() => {
    const d = (e) => { if (e.key === "Shift") shiftRef.current = true; };
    const u = (e) => { if (e.key === "Shift") shiftRef.current = false; };
    const blur = () => { shiftRef.current = false; };
    window.addEventListener("keydown", d); window.addEventListener("keyup", u); window.addEventListener("blur", blur);
    return () => { window.removeEventListener("keydown", d); window.removeEventListener("keyup", u); window.removeEventListener("blur", blur); };
  }, []);
  // leaving FURNISH puts the tools away; a tool and a held piece never coexist
  useEffect(() => { if (!furnishing) { setArchTool(null); setHeldDeed(null); archLast.current = null; } }, [furnishing]);
  useEffect(() => { if (archTool !== "deed") setConfirmHome(false); }, [archTool]);
  const pickTool = useCallback((t) => {
    sfx.ui();
    setArchTool((cur) => (cur === t ? null : t));
    setHeldDeed(null); setHeldItem(null); setMoving(null); setAim(null); setPickedDecor(null);
    archLast.current = null;
  }, []);
  const archFinishFor = (tool) => {
    if (tool === TOOL.FLOOR) return ITEMS[archFinish]?.kind === KIND.HALL_FLOOR ? archFinish : null;
    if (tool === TOOL.WALL || tool === TOOL.DOOR || tool === TOOL.PAINT) return ITEMS[archFinish]?.kind === KIND.HALL_WALL ? archFinish : null;
    return null;
  };
  const archAtX = archOn ? (aim ? Math.round(aim.x) : facingTile(pos, facing, 1).x) : null;
  const archAtY = archOn ? (aim ? Math.round(aim.y) : facingTile(pos, facing, 1).y) : null;
  const archAt = useMemo(() => (archAtX == null ? null : { x: archAtX, y: archAtY }), [archAtX, archAtY]);
  // who is standing where — nobody is walled in where they stand
  const standingOn = useCallback((x, y) => {
    if (posRef.current.x === x && posRef.current.y === y) return "You are standing there.";
    for (const d of Object.values(otherPlayersRef.current || {})) {
      if (d && Math.round(d.x) === x && Math.round(d.y) === y) return `${d.name || "A visitor"} is standing there.`;
    }
    return null;
  }, []);
  const deedName = (id) => { const d = getAchievementDisplay(id, earnedOf(id)); return d.masked ? "SEALED DEED" : (d.name || "the deed"); };
  // the deed on a tile: a floor deed on it, or — facing the north wall, or
  // on the front row under it — the painting hung there
  const deedAtTile = useCallback((x, y) => {
    const c = hallNow;
    if (y <= HALL_TOP) return c.trophies.find((t) => t.mount === "wall" && t.x === x) || null;
    const floor = c.trophies.find((t) => t.mount !== "wall" && t.x === x && t.y === y);
    if (floor) return floor;
    if (y === FLOOR_TOP) return c.trophies.find((t) => t.mount === "wall" && t.x === x && t.y === HALL_TOP) || null;
    return null;
  }, [hallNow]);
  // where a held deed lands: aimed at the north wall, a painting hangs;
  // anywhere else it stands on the floor
  const deedDest = (x, y) => (y <= HALL_TOP ? { x, y: HALL_TOP, hang: true } : { x, y, hang: false });
  const archVerdict = useMemo(() => {
    if (!archOn || !archAt) return null;
    const { x, y } = archAt;
    if (archTool === "deed") {
      if (!heldDeed) {
        const d = deedAtTile(x, y);
        if (!d) return { ok: false, why: "No deed there — face one, or click it." };
        return { ok: true, label: `PICK UP · ${deedName(d.id)}` };
      }
      const dest = deedDest(x, y);
      const why = deedSpotOk(getHallBase(), interiorNow, holding.props, heldDeed, dest.x, dest.y);
      return why === true ? { ok: true, label: dest.hang ? "HANG IT HERE" : "STAND IT HERE" } : { ok: false, why };
    }
    const stand = archTool === TOOL.WALL ? standingOn(x, y) : null;
    if (stand) return { ok: false, why: stand };
    const why = interiorCellOk(getHallBase(), interiorNow, holding.props, archTool, x, y, archFinishFor(archTool), owned);
    const k0 = `${x},${y}`;
    const isNew = (archTool === TOOL.WALL || archTool === TOOL.DOOR) && !interiorNow.walls[k0] && !interiorNow.doors[k0];
    const price = isNew ? ` · ✹${BUILD_COST_UD}` : "";
    const verb = { wall: `BUILD A WALL${price}`, door: `MAKE A DOORWAY${price}`, erase: "KNOCK IT DOWN", floor: archFinishFor(TOOL.FLOOR) ? "PAINT THE FLOOR" : "SCRUB THE FLOOR", paint: "PAINT THE WALL" }[archTool];
    return why === true ? { ok: true, label: verb } : { ok: false, why };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archOn, archAt, archTool, archFinish, heldDeed, interiorNow, holding.props, owned, deedAtTile, standingOn]);
  const cursorOk = !!archVerdict?.ok;
  const archCursor = useMemo(() => (archAt ? { x: archAt.x, y: archAt.y, ok: cursorOk, deed: archTool === "deed" && !!heldDeed } : null),
    [archAt, cursorOk, archTool, heldDeed]);

  // a straight run from the last tile built to this one (not counting it)
  const lineCells = (a, b) => {
    if (a.x !== b.x && a.y !== b.y) return null;
    const out = [];
    const dx = Math.sign(b.x - a.x), dy = Math.sign(b.y - a.y);
    for (let x = a.x + dx, y = a.y + dy, n = 0; n < 60; n++, x += dx, y += dy) {
      out.push([x, y]);
      if (x === b.x && y === b.y) break;
    }
    return out.length ? out : [[b.x, b.y]];
  };
  const pushArchUndo = (entry) => {
    archUndoRef.current = [...archUndoRef.current.slice(-29), entry];
    setArchUndoN(archUndoRef.current.length);
  };
  const clearArchUndo = () => { archUndoRef.current = []; setArchUndoN(0); };
  const lock = async (fn) => {
    if (archBusy.current) return;
    archBusy.current = true; setDecorBusy(true);
    try { await fn(); } finally { archBusy.current = false; setDecorBusy(false); }
  };

  const doArchAt = useCallback((at) => lock(async () => {
    if (!archOn || !at) return;
    const { x, y } = at;
    const cur = interiorRef.current;
    if (archTool === "deed") {
      if (!heldDeed) {
        const d = deedAtTile(x, y);
        if (!d) { decorSay(false, "No deed there."); return; }
        setHeldDeed(d.id); sfx.ui();
        decorSay(true, "Deed in hand — face or click where it goes (the north wall hangs a painting). ESC puts it back.");
        return;
      }
      const dest = deedDest(x, y);
      const was = cur.deeds[heldDeed] || null;
      const r = await moveDeed(viewerUid, getHallBase(), cur, holding.props, heldDeed, dest.x, dest.y);
      if (!r.ok) { decorSay(false, r.message); return; }
      const next = { ...cur, deeds: { ...cur.deeds } };
      if (r.home) delete next.deeds[heldDeed]; else next.deeds[heldDeed] = `${dest.x},${dest.y}`;
      interiorRef.current = normaliseInterior(next);
      pushArchUndo({ kind: "deed", id: heldDeed, was, after: interiorKey(interiorRef.current) });
      setHeldDeed(null); sfx.ui();
      return;
    }
    const lined = (shiftRef.current || archLine) && archLast.current;
    let cells = [[x, y]];
    if (lined) {
      const run = lineCells(archLast.current, { x, y });
      if (!run) { decorSay(false, "A line runs straight — along a row or down a column."); return; }
      cells = run;
    }
    const skippedHere = [];
    if (archTool === TOOL.WALL) {
      cells = cells.filter(([cx, cy]) => { const w = standingOn(cx, cy); if (w) skippedHere.push(w); return !w; });
      if (!cells.length) { decorSay(false, skippedHere[0]); return; }
    }
    const prev = cells.map(([cx, cy]) => {
      const k = `${cx},${cy}`;
      return { x: cx, y: cy, wall: cur.walls[k] || null, door: cur.doors[k] || null, floor: cur.floors[k] || null };
    });
    // building costs ✹1 a tile; undo and clearing a shut Hall never charge
    const r = await buildCells(viewerUid, getHallBase(), cur, holding.props, archTool, cells, { finish: archFinishFor(archTool), owned, charge: true });
    if (!r.ok) { decorSay(false, r.message); return; }
    interiorRef.current = r.interior;
    const done = new Set(cells.map(([cx, cy]) => `${cx},${cy}`));
    for (const sk of r.skipped || []) done.delete(`${sk.x},${sk.y}`);
    pushArchUndo({ kind: "cells", prev: prev.filter((p) => done.has(`${p.x},${p.y}`)), after: interiorKey(r.interior) });
    if (done.has(`${x},${y}`)) archLast.current = { x, y };
    sfx.ui();
    const miss = (r.skipped || []).length + skippedHere.length;
    if (miss) decorSay(false, `${miss} tile${miss > 1 ? "s" : ""} left alone — ${(r.skipped?.[0]?.why) || skippedHere[0]}`);
    else if (r.paid) decorSay(true, `Built — ✹${r.paid}.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [archOn, archTool, archFinish, archLine, heldDeed, viewerUid, holding.props, owned, deedAtTile, standingOn, decorSay]);

  // ⚑ UNDO puts the tiles back the way they were — through the same rules
  // (a wall is not restored through a chair put there since), and only onto
  // the Hall it was taken from (a layout or another device since → refused)
  const doArchUndo = useCallback(() => lock(async () => {
    const e = archUndoRef.current[archUndoRef.current.length - 1];
    if (!e) return;
    const base = getHallBase();
    let cur = interiorRef.current;
    if (interiorKey(cur) !== e.after) {
      clearArchUndo();
      decorSay(false, "The Hall has changed since then — there is nothing safe to undo.");
      return;
    }
    let fails = 0, total = 0;
    if (e.kind === "deed") {
      total = 1;
      const t = hallNow.trophies.find((d) => d.id === e.id);
      const [hx, hy] = e.was ? e.was.split(",").map(Number) : [t?.home.x, t?.home.y];
      const r = await moveDeed(viewerUid, base, cur, holding.props, e.id, hx, hy);
      if (r.ok) { const n = { ...cur, deeds: { ...cur.deeds } }; if (e.was) n.deeds[e.id] = e.was; else delete n.deeds[e.id]; cur = normaliseInterior(n); }
      else fails++;
    } else {
      const k = (p) => `${p.x},${p.y}`;
      const steps = { erase: [], wall: new Map(), door: new Map(), paint: new Map(), floor: new Map() };
      const add = (m, f, c) => m.set(f, [...(m.get(f) || []), c]);
      for (const p of e.prev) {
        const nowWall = cur.walls[k(p)] || null, nowDoor = cur.doors[k(p)] || null, nowFloor = cur.floors[k(p)] || null;
        const cell = [p.x, p.y];
        if ((p.floor || null) !== nowFloor) add(steps.floor, p.floor || "", cell);
        if (!p.wall && !p.door) { if (nowWall || nowDoor) steps.erase.push(cell); continue; }
        const want = p.wall ? "wall" : "door", fin = p.wall || p.door;
        const has = nowWall ? "wall" : nowDoor ? "door" : null;
        if (has !== want) add(steps[want], "", cell);
        if (has !== want || (nowWall || nowDoor) !== fin) add(steps.paint, fin, cell);
      }
      const run = async (tool, cells, fin) => {
        let list = cells;
        if (tool === TOOL.WALL) list = cells.filter(([cx, cy]) => !standingOn(cx, cy));
        total += cells.length; fails += cells.length - list.length;
        if (!list.length) return;
        const r = await buildCells(viewerUid, base, cur, holding.props, tool, list, { finish: fin, owned });
        if (r.ok) { cur = r.interior; fails += (r.skipped || []).length; } else fails += list.length;
      };
      // down first, then walls, then the doorways in them, then the paint
      if (steps.erase.length) await run(TOOL.ERASE, steps.erase, null);
      for (const [, cells] of steps.wall) await run(TOOL.WALL, cells, null);
      for (const [, cells] of steps.door) await run(TOOL.DOOR, cells, null);
      for (const [fin, cells] of steps.paint) await run(TOOL.PAINT, cells, fin === 1 ? null : fin);
      for (const [fin, cells] of steps.floor) await run(TOOL.FLOOR, cells, fin || null);
    }
    interiorRef.current = cur;
    archLast.current = null;
    if (total && fails >= total) { decorSay(false, "It could not go back — something is in the way now. (Z again once it can.)"); return; }
    archUndoRef.current = archUndoRef.current.slice(0, -1);
    const below = archUndoRef.current[archUndoRef.current.length - 1];
    // what is left of the stack only holds if this undo put the Hall back exactly
    if (below && interiorKey(cur) !== below.after) archUndoRef.current = [];
    setArchUndoN(archUndoRef.current.length);
    if (fails) decorSay(false, "Some of it could not go back — something is in the way now.");
    else sfx.ui();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [viewerUid, holding.props, owned, hallNow, standingOn, decorSay]);

  const doResetDeeds = useCallback(() => lock(async () => {
    if (!isOwnHome) return;
    if (!confirmHome) { setConfirmHome(true); decorSay(true, "Press EVERY DEED HOME again to send them all back — this cannot be undone."); return; }
    setConfirmHome(false);
    const r = await resetDeeds(viewerUid, getHallBase(), interiorRef.current, holding.props);
    if (r.ok) {
      setHeldDeed(null);
      interiorRef.current = normaliseInterior({ ...interiorRef.current, deeds: {} });
      clearArchUndo();
      decorSay(true, "Every deed is back where the Hall first put it.");
    } else decorSay(false, r.message);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isOwnHome, confirmHome, viewerUid, holding.props, decorSay]);

  // ════════════════════════════════════════════════════════════════
  // ⭐ OW-42 · LAYOUTS & DESIGNS — the whole inside at once
  // ────────────────────────────────────────────────────────────────
  // A layout you own, one of your three designs, the Hall a friend let you
  // copy, a plain Hall, or PUT IT BACK. Each asks twice (furniture in the
  // way goes to the shed), and each can be put back.
  // ════════════════════════════════════════════════════════════════
  const [designsOpen, setDesignsOpen] = useState(false);
  const anyModalNow = !!inspect || !!showBook || !!pickerSlot || !!frameInspect;
  const [confirmApply, setConfirmApplyRaw] = useState(null);
  // an armed "SURE?" lasts a few seconds, never days
  const confirmTimer = useRef(0);
  const setConfirmApply = useCallback((k) => {
    window.clearTimeout(confirmTimer.current);
    setConfirmApplyRaw(k);
    if (k) confirmTimer.current = window.setTimeout(() => setConfirmApplyRaw(null), 4500);
  }, []);
  useEffect(() => () => window.clearTimeout(confirmTimer.current), []);
  const [designName, setDesignName] = useState("");
  const designs = useMemo(() => normaliseDesigns(holding.designsRaw), [holding.designsRaw]);
  const layoutsOwned = useMemo(() => Object.values(ITEMS).filter((i) => i.kind === KIND.HALL_LAYOUT && owned[i.id]), [owned]);
  const layoutsCount = useMemo(() => Object.values(ITEMS).filter((i) => i.kind === KIND.HALL_LAYOUT).length, []);
  useEffect(() => { setConfirmApply(null); }, [furnishing, designsOpen, archTool, anyModalNow, setConfirmApply]);
  const doApplyWhole = useCallback((key, label, run) => lock(async () => {
    if (!isOwnHome) return;
    if (confirmApply !== key) {
      setConfirmApply(key);
      decorSay(true, `Press ${label} again to rebuild the Hall — furniture in the way goes back to your shed. PUT IT BACK undoes the walls.`);
      return;
    }
    setConfirmApply(null);
    decorSay(true, "Building…");
    const r = await run();
    if (!r.ok) { decorSay(false, r.message); return; }
    if (r.interior) interiorRef.current = r.interior;
    // pieces that went to the shed are no longer anything to select, move or undo
    clearArchUndo(); archLast.current = null; setHeldDeed(null);
    if ((r.lifted || []).length) { setMoving(null); setPickedDecor(null); setAim(null); undo.clear?.(); }
    const n = (r.lifted || []).length;
    sfx.ui();
    decorSay(true, `${r.name || "Done"} — built.${n ? ` ${n} piece${n > 1 ? "s" : ""} went back to your shed.` : ""} PUT IT BACK swaps the walls back (not the furniture).`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isOwnHome, confirmApply, decorSay, setConfirmApply, undo]);
  const applyOpts = { hangOk: hangingFits };
  const doApplyLayout = (id) => doApplyWhole(`L:${id}`, "APPLY", () => applyLayout(viewerUid, getHallBase(), id, applyOpts));
  const doApplySlot = (slot) => doApplyWhole(`S:${slot}`, slot === LAST_SLOT ? "PUT IT BACK" : "APPLY", () => applyDesign(viewerUid, getHallBase(), slot, holding.designsRaw, applyOpts));
  const doApplyPlain = () => doApplyWhole("plain", "PLAIN HALL", () => applyPlain(viewerUid, getHallBase(), applyOpts));
  const saveSlotNow = (slot) => lock(async () => {
    const r = await saveDesign(viewerUid, slot, interiorRef.current, designName.trim() || designs[slot]?.name || "");
    if (r.ok) { sfx.ui(); decorSay(true, `Saved as “${r.name}”.`); setDesignName(""); }
    else decorSay(false, r.message);
  });
  const doSaveSlot = (slot) => {
    // saving OVER a design asks twice, like clearing it
    if (designs[slot] && confirmApply !== `O:${slot}`) { setConfirmApply(`O:${slot}`); decorSay(true, "Press SAVE OVER again to replace that design."); return; }
    setConfirmApply(null);
    return saveSlotNow(slot);
  };
  const doClearSlot = (slot) => {
    if (confirmApply !== `C:${slot}`) { setConfirmApply(`C:${slot}`); decorSay(true, "Press CLEAR again to forget that design."); return; }
    setConfirmApply(null);
    lock(async () => { const r = await clearDesign(viewerUid, slot); if (!r.ok) decorSay(false, r.message); });
  };
  // ⭐ COPY THIS HALL — a guest takes the walls home (never the deeds)
  const hallHasBuild = !hallNow.broken && !hallNow.future && hallNow.walls.length + hallNow.doors.length + hallNow.floors.length > 0;
  const doCopyHall = useCallback(() => lock(async () => {
    if (isOwnHome || !viewerUid || !ownerUid) return;
    if (confirmApply !== "copy") { setConfirmApply("copy"); decorSay(true, "Press COPY THIS HALL again — it replaces whatever is in your Copied design."); return; }
    setConfirmApply(null);
    const r = await copyHallFrom(viewerUid, ownerUid, ownerName);
    if (r.ok) { sfx.ui(); decorSay(true, `${r.name} is in your Copied slot — build it at home: ⚒ FURNISH › LAYOUTS & DESIGNS.`); }
    else decorSay(false, r.message);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isOwnHome, viewerUid, ownerUid, ownerName, decorSay, confirmApply, setConfirmApply]);
  const designsKey = useMemo(() => Object.entries(designs).map(([k, d]) => `${k}:${d.at}:${interiorKey(d.interior)}`).join("|"), [designs]);
  const designHalls = useMemo(() => {
    const out = {};
    if (!isOwnHome || !designsOpen) return out;
    for (const [slot, d] of Object.entries(designs)) out[slot] = composeHall(getHallBase(), d.interior, { peek: true });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designsKey, isOwnHome, designsOpen]);
  const layoutsOwnedPlanned = layoutsOwned.filter((i) => planOf(i.id));

  // ⭐ NOBODY IS LEFT INSIDE A WALL. A layout, a design or another device can
  // put stone where you stand; the walk only tests the tile you step ONTO,
  // so you are moved to the nearest open tile (review, OW-42 E2E).
  useEffect(() => {
    const p = posRef.current;
    if (!p || hallNow.isWalkable(p.x, p.y)) return;
    let best = null, bd = Infinity;
    for (const k of hallNow.reach) {
      const [x, y] = k.split(",").map(Number);
      const d = Math.abs(x - p.x) + Math.abs(y - p.y);
      if (d < bd) { bd = d; best = { x, y }; }
    }
    const to = best || SPAWN;
    posRef.current = to;
    setPos(to);
    syncPos(to.x, to.y, 0, 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallNow]);

  // ⭐ a Hall stored shut (two devices, or a Hall from before a new wing)
  // shows its sealing walls faded red; this knocks exactly those down
  const doClearSealing = useCallback(() => lock(async () => {
    const cells = sealingWalls(getHallBase(), interiorRef.current);
    if (!cells.length) return;
    const r = await buildCells(viewerUid, getHallBase(), interiorRef.current, holding.props, TOOL.ERASE, cells, {});
    if (r.ok) { interiorRef.current = r.interior; clearArchUndo(); decorSay(true, "The walls that shut the Hall are down — the rest stands again."); }
    else decorSay(false, r.message);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [hallNow, viewerUid, holding.props, decorSay]);

  // the finishes a tool may paint with (yours, the free ones, your layouts')
  const paintChoices = useMemo(() => ({
    floor: Object.values(ITEMS).filter((i) => i.kind === KIND.HALL_FLOOR && finishAllowed(owned, i.id)),
    wall: Object.values(ITEMS).filter((i) => i.kind === KIND.HALL_WALL && finishAllowed(owned, i.id)),
  }), [owned]);

  // ⭐ OW-24 · COLLECTING INDOORS. `dawg_bowl` is a GROWER and its `areas`
  // include the Hall, so a player can absolutely plant one in their front
  // room — and before this, there was no way to collect from it in here.
  // ⛑ The Dawgs were never STRANDED (the yard's COLLECT ALL sweeps the
  // whole holding, both rooms, because `pendingTotal` and `harvestAll`
  // walk every grower regardless of area). But "walk outside to collect
  // from the bowl in your living room" is not a rule anybody would guess,
  // so the reach in here does it too.
  const doHarvestDecor = useCallback(async (propId) => {
    if (!isOwnHome || decorBusy || !propId) return;
    setDecorBusy(true);
    try {
      const r = await harvest(viewerUid, propId);
      if (r.ok) {
        const at = resolvePlacement(holdingRef.current.props, propId);
        if (at) burst(at.x, at.y, r.paidUD);
        sfx.harvest(r.paidUD);
        decorSay(true, r.message || `Collected ✹${r.paidUD}.`);
      } else decorSay(false, r.message || "Nothing ripe there yet.");
    } finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, viewerUid, decorSay, burst]);

  // What the piece you are facing is, and what it is worth right now.
  // One place, so the bubble, the key and the button cannot disagree
  // about whether E collects or picks up.
  const decorAction = useMemo(() => {
    if (!activeDecor) return null;
    const p = holding.props?.[activeDecor];
    const it = p && ITEMS[p.itemId];
    if (!it) return null;
    if (it.kind === KIND.GROWER) {
      const g = growthOf(it, holding.growers?.[activeDecor], serverNow());
      if (g && g.ready > 0) return { kind: "harvest", item: it, ready: g.ready };
    }
    // ⭐ A DOOR INDOORS IS A DOOR (L50, finally honoured 2026-09-15). It
    // was furniture with a PICK UP prompt; now it is the way outside.
    if (it.kind === KIND.DOOR) return { kind: "door", item: it, ready: 0 };
    return { kind: furnishing ? "move" : "pickup", item: it, ready: 0 };
    // `frame` ticks once a second and re-ripens this without a timer of
    // its own — the Hall already has exactly one interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDecor, holding.props, holding.growers, frame, furnishing]);

  const doTurnDecor = useCallback(async (propId) => {
    if (!isOwnHome || decorBusy || !propId) return;
    setDecorBusy(true);
    try {
      // ⭐ OW-34: a long piece turning is a placement — the Hall's walls and
      // fixtures are asked, and a refusal says why instead of doing nothing
      const p = holding.props?.[propId];
      if (p && !p.on) {
        const next = quarterRot(p.rot || 0) + rotStepFor(AREAS.HALL, p.itemId);
        const at = hallCentre(p.x, p.y, dimsOf(p.itemId, next));
        const open = hallStaysOpen(holding.props, { propId, itemId: p.itemId, x: at.x, y: at.y, rot: next });
        if (open !== true) { decorSay(false, open); return; }
      }
      const r = await rotateProp(viewerUid, propId, 1, holding.props, { floorOk: hallFloorOk });
      if (r?.ok) {
        sfx.turn();
        if (r.from) undo.push({ type: "turn", propId, x: r.from.x, y: r.from.y, rot: r.from.rot });
      }
      else if (r?.message) decorSay(false, r.message);
    }
    finally { setDecorBusy(false); }
  }, [isOwnHome, decorBusy, viewerUid, holding.props, decorSay, undo]);

  // R with a piece in hand: a hanging faces the room and does not turn
  const turnHeld = useCallback(() => {
    if (ghostIsWall) { decorSay(false, "A hanging faces the room — aim it at another wall instead."); return; }
    setHeldRot((r) => r + rotStepFor(AREAS.HALL, ghostItem));
    sfx.turn();
  }, [ghostIsWall, ghostItem, decorSay]);

  // ── OW-24 · E / R / ESC, once per press ────────────────────
  // ⚠ Deliberately LAST in priority: E already opens the Deed Book, a
  // trophy, a frame and the yard door on this screen. Furnishing only
  // claims the key while you are actually holding something or standing
  // in front of your own furniture, so nothing that worked yesterday
  // stops working today.
  useEffect(() => {
    if (!isOwnHome) return undefined;
    const onKey = (e) => {
      if (inspect || showBook || pickerSlot || frameInspect) return;
      if (e.repeat || isTypingTarget(e)) return;
      const k = e.key.toLowerCase();
      // ⭐ OW-42 · a tool is out: E builds, Z undoes the build, ESC puts it away
      if (archOn) {
        if (k === "e") { e.preventDefault(); doArchAt(archAt); return; }
        if (k === "z") { e.preventDefault(); doArchUndo(); return; }
        if (k === "escape") { if (heldDeed) setHeldDeed(null); else if (aim) setAim(null); else setArchTool(null); return; }
        if (k === "q" || k === "r") return;
      }
      if (k === "escape" && (heldItem || movingPropId || pickedDecor || aim)) { setHeldItem(null); setMoving(null); setPickedDecor(null); setAim(null); return; }
      if (k === "z" && furnishing) { e.preventDefault(); doUndoDecor(); return; }
      // ⚠ Q is also the fixture key (the deed/frame/book pill). Only pick up
      // with it when no fixture is under you, or one press does both.
      if (k === "q" && furnishing && activeDecor && !ghostItem && !nearTrophy && !nearBook && !nearFrame && !nearYardDoor) { doPickUpDecor(activeDecor); return; }
      if (k === "e") {
        if (ghostItem) { doPlaceDecor(); return; }
        if (activeDecor && decorAction?.kind === "door") { sfx.door(); onEnterMinerium?.(); return; }
        if (activeDecor && decorAction?.kind === "move") { startMoveDecor(activeDecor); return; }
        // ⚠⚠ NO FIXTURE GATING HERE, AND THE REASON MATTERS.
        // This used to also require `!nearTrophy && !nearBook &&
        // !nearFrame && !nearYardDoor`, on the assumption that E was
        // contested. It is not: every fixture in this Hall is a
        // `ProximityBubble`, and those bind **Q and Enter**, never E.
        // The gate bought nothing and cost a great deal — trophy and
        // frame stand-tiles cover most of the nine walkable rows in
        // every wing, so furniture placed on them could be put down and
        // then NEVER PICKED UP AGAIN, with no bubble, no key and no
        // message saying why. Gone from the shed, stuck in the floor.
        // ⛑ E is the furnishing key; Q is the fixture key. They do not
        // overlap and must not pretend to. Found by adversarial review
        // 2026-09-13.
        if (activeDecor) {
          if (decorAction?.kind === "harvest") doHarvestDecor(activeDecor);
          else doPickUpDecor(activeDecor);
        }
        return;
      }
      if (k === "r") {
        if (ghostItem) { turnHeld(); return; }
        // turning what is standing there is a build action — FURNISH first
        if (activeDecor && furnishing) doTurnDecor(activeDecor);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // ⚠ `heldRot` IS A LOAD-BEARING DEP, not tidiness — `doPlaceDecor`
    // closes over it. Without it you turn a piece with R, place it with
    // E, and it lands at the angle it had before you turned it, because
    // the listener that fired belongs to the render where the old
    // rotation was still current. (The same trap `MineriumScreen`
    // documents on its own key effect.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnHome, inspect, showBook, pickerSlot, frameInspect,
      heldItem, heldRot, nearDecor, nearYardDoor, nearFrame, nearTrophy, nearBook,
      decorAction, doPlaceDecor, doPickUpDecor, doTurnDecor, doHarvestDecor,
      movingPropId, pickedDecor, aim, furnishing, activeDecor, ghostItem, doUndoDecor, startMoveDecor, onEnterMinerium, turnHeld,
      archOn, archAt, heldDeed, doArchAt, doArchUndo]);

  // ── taps on the world, from the renderer (by ref: Hall3D is memo'd) ──
  const tapRef = useRef({});
  tapRef.current = {
    ground: (x, y) => {
      if (!isOwnHome || !furnishing || anyModalRef.current) return;
      // ⭐ OW-42: with a tool out, a tap IS the build (aim, then act there)
      if (archOn) { const at = { x: Math.round(x), y: Math.max(HALL_TOP, Math.round(y)) }; setAim(at); doArchAt(at); return; }
      if (!ghostItem) { setPickedDecor(null); return; }
      setAim({ x, y });
    },
    prop: (propId, point = null) => {
      if (!isOwnHome || anyModalRef.current) return;
      if (archOn) {
        // the tile under the pointer, not the piece's centre (a 2-wide rug's
        // centre sits on a tile edge)
        const at = point ? { x: point.x, y: point.y } : resolvePlacement(holding.props, propId);
        if (at) { const t = { x: Math.round(at.x), y: Math.round(at.y) }; setAim(t); doArchAt(t); }
        return;
      }
      if (furnishing && ghostItem && !movingPropId) {
        const at = resolvePlacement(holding.props, propId);
        if (at) setAim({ x: at.x, y: at.y });
        return;
      }
      setPickedDecor((cur) => (cur === propId ? null : propId));
      sfx.ui();
    },
    // ⭐ OW-42: a deed clicked with the deed tool out
    deed: (id) => {
      if (!isOwnHome || !archOn || archTool !== "deed" || anyModalRef.current) return;
      const t = hallNow.trophies.find((d) => d.id === id);
      if (!t) return;
      const at = { x: t.x, y: t.y };
      setAim(at); doArchAt(at);
    },
  };


  // Local + remote players for rendering.
  const allPlayers = [
    {
      uid: viewerUid, isLocal: true,
      data: { x: pos.x, y: pos.y, facingX: facing.x, facingY: facing.y, gender, companionId, name: viewerName },
    },
    ...Object.entries(otherPlayers).map(([uid, d]) => ({ uid, isLocal: false, data: d })),
  ];

  const nearTrophyEarned = nearTrophy ? earnedOf(nearTrophy.id) : false;
  // In your own home you can inspect empty pedestals too (see the chase).
  // In a visitor's home, only earned trophies are interactable.
  const trophyInteractable = nearTrophy && (isOwnHome || nearTrophyEarned);

  // ── THE 3D INTERACTION PILL ─────────────────────────────────
  // The 2D bubbles are placed in TILE space, which means nothing once the
  // camera can stand anywhere. In 3D the SAME proximity state and the SAME
  // handlers drive one fixed pill (UnstablePierre's pattern). Fixture first,
  // in the bubbles' own priority; your furniture beside it, so a chair on a
  // trophy's tile is always reachable, which is what the 2D "E still
  // reaches" hint exists to promise.
  const anyModal = !!inspect || showBook || !!pickerSlot || !!frameInspect;
  anyModalRef.current = anyModal;
  const pillActions = [];
  if (is3D && !anyModal) {
    if (trophyInteractable) {
      const d = getAchievementDisplay(nearTrophy.id, nearTrophyEarned);
      pillActions.push({ key: "fixture", color: nearTrophy.tint,
        label: `INSPECT · ${d.masked ? "SEALED DEED" : String(d.name).toUpperCase()}`,
        run: () => setInspect(nearTrophy) });
    } else if (nearBook) {
      pillActions.push({ key: "fixture", color: SD.gold, label: "OPEN · DEED BOOK", run: () => setShowBook(true) });
    } else if (nearYardDoor && onEnterMinerium) {
      pillActions.push({ key: "fixture", color: SD.goldBright,
        label: `GO OUTSIDE · ${isOwnHome ? "THE MINERIUM" : `${(ownerName || "THEIR").toUpperCase()}'S MINERIUM`}`,
        run: () => onEnterMinerium() });
    } else if (nearFrame) {
      const pieceId = decorMap[nearFrame.slotId];
      const piece = pieceId ? galleryById[pieceId] : null;
      if (piece) {
        pillActions.push({ key: "fixture", color: piece.prov?.color || SD.gold,
          label: `INSPECT · ${String(piece.title).toUpperCase()}`,
          run: () => setFrameInspect({ slotId: nearFrame.slotId, piece }) });
      } else if (isOwnHome) {
        pillActions.push({ key: "fixture", color: nearFrame.tint, label: "HANG · EMPTY FRAME", run: () => setPickerSlot(nearFrame) });
      }
    }
    if (activeDecor && !ghostItem && decorAction?.kind === "door" && onEnterMinerium) {
      pillActions.push({ key: "decor", hint: "E", color: SD.goldBright,
        label: `GO OUTSIDE · ${decorAction.item.name.toUpperCase()}`, run: () => { sfx.door(); onEnterMinerium(); } });
    } else if (isOwnHome && activeDecor && !ghostItem && decorAction && !archOn) {
      const ripe = decorAction.kind === "harvest";
      const tint = (SHELF_META[decorAction.item.shelf] || SHELF_META.fable).tint;
      const name = decorAction.item.name.toUpperCase();
      if (ripe) {
        pillActions.push({ key: "decor", hint: "E", color: "#9ad46a", label: `COLLECT · ✹${decorAction.ready}`, run: () => doHarvestDecor(activeDecor) });
      } else if (decorAction.kind === "move") {
        pillActions.push({ key: "decor", hint: "E", color: tint, label: `MOVE · ${name}`, run: () => startMoveDecor(activeDecor) });
        pillActions.push({ key: "pickup", hint: (nearTrophy || nearBook || nearFrame || nearYardDoor) ? "" : "Q", color: "#e8a0a0", label: "PICK UP", run: () => doPickUpDecor(activeDecor) });
      } else {
        pillActions.push({ key: "decor", hint: "E", color: tint, label: `PICK UP · ${name}`, run: () => doPickUpDecor(activeDecor) });
      }
      if (!ripe && furnishing && layerOf(holding.props?.[activeDecor]?.itemId) !== LAYER.WALL) pillActions.push({ key: "turn", hint: "R", color: SD.starSilver, label: "↻ TURN", run: () => doTurnDecor(activeDecor) });
    }
  }
  // Q / Enter work the fixture, exactly as the 2D ProximityBubble binds them.
  // E and R stay with the furnishing effect above.
  const pillFixtureRef = useRef(null);
  pillFixtureRef.current = pillActions.find((a) => a.key === "fixture") || null;
  const pillShownAt = useRef(0);
  const pillFixtureKey = pillFixtureRef.current ? pillFixtureRef.current.label : "";
  useEffect(() => { pillShownAt.current = Date.now(); }, [pillFixtureKey]);
  useEffect(() => {
    if (!is3D) return undefined;
    const onKey = (e) => {
      if (e.key !== "q" && e.key !== "Q" && e.key !== "Enter") return;
      // never while typing (a DM, the chat), and one press is one action
      if (e.repeat || e.target?.closest?.('input,textarea,select,[contenteditable="true"]')) return;
      const a = pillFixtureRef.current;
      // the bubble's 400ms grace, so walking onto a tile with Q held does not fire it
      if (!a || Date.now() - pillShownAt.current < 400) return;
      e.preventDefault();
      a.run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const title = isOwnHome ? "YOUR HALL OF DEEDS" : `${(ownerName || "A WANDERER").toUpperCase()}'S HALL`;
  // the worn finishes, as tints for the renderer (stable identity per pair)
  // ⭐ OW-34: the worn light rides the same object — its flame and pool
  // colours reach every sconce on the stone; the mood itself (the three
  // lights, the fog, the pulse) goes to the scene as `mood`.
  // ⭐ THE CHANGING-ROOM MIRROR (E2E review): hovering a finish or a light
  // you do not own yet shows it on YOUR Hall until the pointer leaves. It
  // is local only — nothing is written, and a visitor never sees it.
  const [tryOn, setTryOn] = useState(null);
  const tryKind = ITEMS[tryOn]?.kind;
  const wornLight = tryKind === KIND.HALL_LIGHT ? tryOn : holding.hallLight;
  const wornFloor = tryKind === KIND.HALL_FLOOR ? tryOn : holding.hallFloor;
  const wornWall  = tryKind === KIND.HALL_WALL  ? tryOn : holding.hallWall;
  const [looksOpen, setLooksOpen] = useState(() => !isMobile());
  // a try-on never outlives the panel it was chosen in
  useEffect(() => { if (!furnishing || anyModal) setTryOn(null); }, [furnishing, anyModal]);
  const hallMood = useMemo(() => moodOf(wornLight) || moodOf(FOUNDING_LIGHT), [wornLight]);
  const hallFinish = useMemo(() => {
    const f = ITEMS[wornFloor]?.finish, w = ITEMS[wornWall]?.finish;
    return { floorTint: f?.tint || null, runnerTint: f?.runner || null, wallTint: w?.tint || null,
             floorPattern: f?.pattern || null, wallPattern: w?.pattern || null,
             flameTint: hallMood?.flame || null, poolTint: hallMood?.pool || null,
             rayTint: hallMood?.rays || null, rayBoost: hallMood?.rayBoost ?? 1 };
  }, [wornFloor, wornWall, hallMood]);
  const finishes = useMemo(() => Object.values(ITEMS).filter((i) =>
    i.kind === KIND.HALL_FLOOR || i.kind === KIND.HALL_WALL || i.kind === KIND.HALL_LIGHT), []);

  return (
    <div style={{
      position: "fixed", inset: 0, overflow: "hidden",
      background: "#0c0a10", fontFamily: SD.fontDisplay,
    }}>
      {/* ── THE 3D HALL ─────────────────────────────────────────────
          A RENDERER SWAP, and nothing else. Same map, same refs, same
          Firebase, same HUD, same modals — the Hall does not notice which
          one is mounted. Every prop is a ref or a memoised value, because
          Hall3D is React.memo'd and this component re-renders on each step. */}
      {is3D && (
        <Hall3D
          layout={hallLayout}
          posRef={posRef}
          facingRef={facingRef}
          gender={gender}
          companionId={companionId}
          playerName={viewerName}
          othersRef={otherPlayersRef}
          rosterKey={rosterKey}
          isOwnHome={isOwnHome}
          earned={earned3D}
          decorMap={decorMap}
          galleryById={galleryById}
          hallProps={hallProps}
          allProps={holding.props}
          growers={holding.growers}
          selectedId={isOwnHome ? activeDecor : null}
          ghostItemId={isOwnHome ? ghostItem : null}
          ghostRot={ghostRot}
          ghostState={ghost?.state || "free"}
          ghostAtRef={ghostAtRef}
          tapRef={tapRef}
          bursts={bursts}
          furnishing={isOwnHome && furnishing}
          movingPropId={movingPropId}
          finish={hallFinish}
          mood={hallMood}
          wallsDown={archOn}
          archCursor={anyModal ? null : archCursor}
          deedPick={archOn && archTool === "deed" && !anyModal}
        />
      )}

      {!is3D && (
      <div style={{
        position: "absolute",
        left: -camOffset.x, top: -camOffset.y,
        width: COLS * tile, height: ROWS * tile,
      }}>
        <HallBackdrop tile={tile} />
        <HallFloor tile={tile} pulse={floorPulse} />
        <WingBanners tile={tile} />
        <FurnishingsShingle tile={tile} frame={frame} />

        {/* M1 — the south-wall gallery frames (hung art + empty frames) */}
        <HallFrames
          decorMap={decorMap}
          galleryById={galleryById}
          isOwnHome={isOwnHome}
          tile={tile}
          frame={frame}
        />

        {/* ⭐ OW-24 — the furniture. Drawn after the frames and before the
            trophies, so a chair can stand in front of a wall painting but
            never in front of a deed. */}
        <PlacedDecor
          props={hallProps}
          allProps={holding.props}
          tile={tile}
          frame={frame}
          selectedId={nearDecor}
        />
        {isOwnHome && heldItem && (
          <DecorGhost itemId={heldItem} at={ghostAt} tile={tile}
                      frame={frame} rot={ghostRot} blocked={ghostBlocked} />
        )}

        {/* Placed trophies — only render a trophy if EARNED, or if it's your
            own home (where unearned show as empty pedestals to chase). A
            visitor sees a clean hall of achievements only. */}
        {hallNow.trophies.map((t) => {
          const earned = earnedOf(t.id);
          if (!earned && !isOwnHome) return null;
          return (
            <TrophyVisual key={t.id} trophy={t} earned={earned} tile={tile} frame={frame} />
          );
        })}

        <DeedBookVisual tile={tile} frame={frame} />

        {/* Players */}
        {allPlayers.map(({ uid, isLocal, data }) => (
          <PlayerEntity
            key={uid}
            pos={{ x: data.x, y: data.y }}
            facing={{ x: data.facingX ?? 0, y: data.facingY ?? 1 }}
            gender={data.gender || "f"}
            walkFrame={isLocal ? walkFrame : false}
            isIdle={isLocal ? isIdle : false}
            idleFrame={isLocal ? idleFrame : false}
            tile={tile}
            companionId={data.companionId || "default"}
            showCompanion={isLocal}
          />
        ))}
        {allPlayers.map(({ uid, isLocal, data }) => (
          <div key={`name-${uid}`} style={{
            position: "absolute",
            left: data.x * tile + tile / 2,
            top: data.y * tile - tile * 0.6,
            transform: "translateX(-50%)",
            whiteSpace: "nowrap", fontSize: 9,
            color: isLocal ? SD.goldBright : SD.starSilver,
            background: "rgba(0,0,0,0.75)",
            padding: "2px 7px", borderRadius: 4,
            border: `1px solid ${isLocal ? SD.gold + "88" : SD.void4}`,
            fontFamily: SD.fontDisplay, zIndex: 11, pointerEvents: "none",
          }}>{data.name || "???"}</div>
        ))}

        {/* Proximity bubbles */}
        {trophyInteractable && !inspect && (() => {
          const def = ACHIEVEMENTS[nearTrophy.id];
          const display = getAchievementDisplay(nearTrophy.id, nearTrophyEarned);
          return (
            <ProximityBubble
              color={nearTrophy.tint}
              verb="Inspect"
              label={display.masked ? "Sealed Deed" : display.name}
              tileX={nearTrophy.x}
              tileY={nearTrophy.y}
              tile={tile}
              portrait={isPortrait()}
              onActivate={() => setInspect(nearTrophy)}
              onDismiss={() => {}}
            />
          );
        })()}

        {nearBook && !inspect && (
          <ProximityBubble
            color={SD.gold}
            verb="Open"
            label="Deed Book"
            tileX={DEED_BOOK.x}
            tileY={DEED_BOOK.y}
            tile={tile}
            portrait={isPortrait()}
            onActivate={() => setShowBook(true)}
            onDismiss={() => {}}
          />
        )}

        {/* ⭐ THE DOOR OUT.
            ⚠ AMENDED 2026-09-13 · OW-23 · caP'n: "yes :333"
            This used to read `isOwnHome &&`, on the reasoning that "a
            visitor is here to look at your deeds, not to walk around
            your farm." caP'n overruled it: friends walk your Minerium.
            ⛑ THE GATE IS DROPPED HERE AND NOWHERE ELSE. Every WRITER in
            `MineriumScreen` is independently gated on its own `isOwner`
            — placing, moving, turning, picking up, harvesting, buying,
            wearing and the companion all refuse a non-owner before they
            touch Firebase — so opening this door grants LOOKING and
            nothing else. If that ever stops being true, it is that file
            that broke, not this line. */}
        {nearYardDoor && onEnterMinerium && !inspect && !pickerSlot && !frameInspect && (
          <ProximityBubble
            color={SD.goldBright}
            verb="Go outside"
            label={isOwnHome ? "The Minerium" : `${ownerName || "Their"}'s Minerium`}
            tileX={YARD_DOOR.x}
            tileY={FURNISHINGS_SHINGLE.y}
            tile={tile}
            portrait={isPortrait()}
            onActivate={() => onEnterMinerium()}
            onDismiss={() => {}}
          />
        )}

        {/* ⭐ OW-24 — furniture prompt. Owner only, and it yields to any
            fixture under the cursor — not because E is contested (it is
            not; fixtures answer Q), but because every bubble on screen
            binds Q, so two visible bubbles means one keypress fires both.
            ⛑ THE KEY IS NOT GATED, ONLY THE BUBBLE. Furniture standing on
            a trophy's tile is always reachable with E even when the
            trophy owns the prompt — which is what stops a piece becoming
            permanently stuck. The floating hint below says so. */}
        {isOwnHome && nearDecor && !heldItem && !inspect && !pickerSlot && !frameInspect
          && !nearYardDoor && !nearBook && !nearTrophy && !nearFrame && (() => {
          const p = holding.props?.[nearDecor];
          const it = decorAction?.item;
          if (!p || !it) return null;
          const spot = resolvePlacement(holding.props, nearDecor) || p;
          const ripe = decorAction.kind === "harvest";
          return (
            <ProximityBubble
              color={ripe ? "#9ad46a" : (SHELF_META[it.shelf] || SHELF_META.fable).tint}
              verb={ripe ? "Collect" : "Pick up"}
              label={ripe
                ? `✹${decorAction.ready} from the ${it.name}`
                : `${it.name}  ·  R turns it`}
              tileX={spot.x}
              tileY={spot.y}
              tile={tile}
              portrait={isPortrait()}
              onActivate={() => (ripe ? doHarvestDecor(nearDecor) : doPickUpDecor(nearDecor))}
              onDismiss={() => {}}
            />
          );
        })()}

        {/* The hint that keeps the line above honest: when a fixture has
            taken the bubble, tell the player E still reaches the piece. */}
        {isOwnHome && nearDecor && !heldItem && !inspect && !pickerSlot && !frameInspect
          && (nearYardDoor || nearBook || nearTrophy || nearFrame) && decorAction && (
          <div style={{
            position: "absolute",
            left: pos.x * tile + tile / 2, top: (pos.y - 1.15) * tile,
            transform: "translateX(-50%)", zIndex: 12, pointerEvents: "none",
            whiteSpace: "nowrap", fontSize: 8.5, letterSpacing: 1,
            background: "rgba(5,3,20,0.86)", border: "1px solid #2c3a22",
            borderRadius: 5, padding: "3px 8px", color: "#9ad46a",
            fontFamily: SD.fontDisplay,
          }}>
            E · {decorAction.kind === "harvest"
              ? `collect ✹${decorAction.ready}`
              : `pick up the ${decorAction.item.name}`}
          </div>
        )}

        {/* Frame prompt. Empty frame in your own hall → Hang. Filled frame →
            Inspect (anyone). Empty frame in a visitor's hall → no prompt. */}
        {nearFrame && !inspect && !pickerSlot && !frameInspect && (() => {
          const pieceId = decorMap[nearFrame.slotId];
          const piece = pieceId ? galleryById[pieceId] : null;
          const filled = !!piece;
          if (!filled && !isOwnHome) return null; // nothing to do at an empty frame you don't own
          return (
            <ProximityBubble
              color={filled ? (piece.prov?.color || SD.gold) : nearFrame.tint}
              verb={filled ? "Inspect" : "Hang"}
              label={filled ? piece.title : "Empty Frame"}
              tileX={nearFrame.x}
              tileY={nearFrame.frameY}
              tile={tile}
              portrait={isPortrait()}
              onActivate={() => {
                if (filled) setFrameInspect({ slotId: nearFrame.slotId, piece });
                else setPickerSlot(nearFrame);
              }}
              onDismiss={() => {}}
            />
          );
        })()}
      </div>
      )}

      {is3D && pillActions.length > 0 && (
        <div style={{
          position: "fixed", left: "50%", bottom: isMobile() ? 200 : 96,
          transform: "translateX(-50%)", zIndex: 120,
          display: "flex", gap: 8, alignItems: "center", justifyContent: "center",
          flexWrap: "wrap", maxWidth: "calc(100vw - 24px)",
          animation: "bubbleFloat 1.8s ease-in-out infinite alternate",
        }}>
          {pillActions.map((a) => (
            <button key={a.key} onClick={a.run}
              disabled={decorBusy && (a.key === "decor" || a.key === "turn" || a.key === "pickup")}
              style={hallPill3D(a.color)}>
              {a.label}
              <span style={{ opacity: 0.55, marginLeft: 8, fontSize: 8.5 }}>{a.hint || "Q"}</span>
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div style={{
        position: "fixed", top: 14, left: "50%", transform: "translateX(-50%)",
        zIndex: 150, textAlign: "center", pointerEvents: "none",
      }}>
        <div style={{
          fontSize: 13, letterSpacing: 4, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: SD.goldBright, textShadow: `0 0 12px ${SD.gold}`,
        }}>{title}</div>
        <div style={{ fontSize: 8, letterSpacing: 3, color: SD.starDim, marginTop: 3 }}>
          W'ILY'S PIER · TROPHY WING
        </div>
      </div>

      {/* Leave button */}
      <div style={{ position: "fixed", top: 14, left: 14, zIndex: 150 }}>
        <button onClick={onLeave} style={{
          background: "rgba(5,3,20,0.9)", border: `1px solid ${SD.void4}`,
          borderRadius: 8, padding: "6px 14px",
          color: SD.starDim, cursor: "pointer",
          fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
        }}>← BACK TO PIER</button>

        {/* ⚑ THE GUARANTEED ROUTE. The shingle is the nice way outside;
            this is the one that cannot fail. A door reachable only by
            standing on one exact tile is a door a player can be shut out
            of by any bug in the proximity test, and being locked out of
            your own holding is not a recoverable experience. */}
        {/* ⚠ OW-23: ungated with the bubble above. A guest who cannot find
            one exact floor tile is shut out of a friend's holding for a
            reason they will never work out, which is the same argument
            that put this button here in the first place. */}
        {onEnterMinerium && (
          <button onClick={onEnterMinerium} style={{
            background: "rgba(5,3,20,0.9)", border: "1px solid #2c3a22",
            borderRadius: 8, padding: "6px 14px", marginLeft: 8,
            color: "#9ad46a", cursor: "pointer",
            fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
          }}>{isOwnHome ? "THE MINERIUM" : "THEIR MINERIUM"} →</button>
        )}
      </div>

      {/* Deed Book catalog button (always available, top-right) */}
      <div style={{ position: "fixed", top: 14, right: 14, zIndex: 150, display: "flex", gap: 8 }}>
        {/* ⭐ OW-24 · FURNISH. Owner only — a guest has nothing to put down
            and every writer would refuse them anyway. */}
        {isOwnHome && (
          <button onClick={() => { sfx.ui(); setFurnishing((v) => !v); setHeldItem(null); setMoving(null); setAim(null); }} style={{
            background: furnishing ? "rgba(154,212,106,0.16)" : "rgba(5,3,20,0.9)",
            border: `1px solid ${furnishing ? "#9ad46a" : "#2c3a22"}`,
            borderRadius: 8, padding: "6px 14px",
            color: "#9ad46a", cursor: "pointer",
            fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
          }}>⚒ FURNISH{furnishing ? " · ON" : ""}</button>
        )}
        {!isOwnHome && viewerUid && hallHasBuild && (
          <button disabled={decorBusy} onClick={doCopyHall} title="Take this Hall's walls, doorways and paint home, into your Copied design" style={{
            background: "rgba(5,3,20,0.9)", border: "1px solid #b388ff66",
            borderRadius: 8, padding: "6px 14px", color: "#b388ff", cursor: "pointer",
            fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
          }}>{confirmApply === "copy" ? "SURE? · COPY THIS HALL" : "⎘ COPY THIS HALL"}</button>
        )}
        <button onClick={() => setShowBook(true)} style={{
          background: "rgba(5,3,20,0.9)", border: `1px solid ${SD.gold}66`,
          borderRadius: 8, padding: "6px 14px",
          color: SD.gold, cursor: "pointer",
          fontSize: 10, letterSpacing: 2, fontFamily: SD.fontDisplay,
        }}>📖 DEED BOOK</button>
      </div>

      {/* ── OW-24 · the shed panel, and what it is telling you to do ── */}
      {isOwnHome && furnishing && !inspect && !showBook && !pickerSlot && !frameInspect && (
        <ShedPanel
          title="THE SHED · INDOORS"
          shed={hallShed}
          held={heldItem}
          busy={decorBusy}
          canUndo={undo.canUndo && !archOn}
          onUndo={doUndoDecor}
          onPick={(id) => { setHeldItem(id); setHeldRot(0); setMoving(null); setAim(null); setArchTool(null); setHeldDeed(null); }}
          onClose={() => { setFurnishing(false); setHeldItem(null); setMoving(null); setAim(null); setTryOn(null); }}
          emptyLine="Nothing in here belongs indoors yet. Ally's counter is out in the yard — GO OUTSIDE and see her."
          style={{ zIndex: 160 }}
          footer={<>
            {activeDecor && holding.props?.[activeDecor] && !ghostItem && !archOn && (
              <div style={{ borderTop: `1px solid ${UI.edge}`, padding: 9 }}>
                <div style={{ ...uiLabel, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                  <Thumb itemId={holding.props[activeDecor].itemId} size={26} />
                  {ITEMS[holding.props[activeDecor].itemId]?.name?.toUpperCase()}
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {layerOf(holding.props[activeDecor].itemId) !== LAYER.WALL && (
                    <button disabled={decorBusy} onClick={() => doTurnDecor(activeDecor)} style={uiBtn(UI.gold)}>TURN · R</button>
                  )}
                  <button onClick={() => startMoveDecor(activeDecor)} style={uiBtn(UI.gold, { on: movingPropId === activeDecor })}>MOVE · E</button>
                  <button disabled={decorBusy} onClick={() => doPickUpDecor(activeDecor)} style={uiBtn(UI.err)}>PICK UP · Q</button>
                </div>
              </div>
            )}
            {/* ⭐ OW-42 · LAYOUTS & DESIGNS — the whole inside at once */}
            <div style={{ borderTop: `1px solid ${UI.edge}`, padding: 9 }}>
              <button onClick={() => setDesignsOpen((v) => !v)}
                      style={{ ...uiLabel, marginBottom: designsOpen ? 6 : 0, background: "none", border: "none", padding: 0, cursor: "pointer", color: UI.dim }}>
                {designsOpen ? "▾" : "▸"} LAYOUTS &amp; DESIGNS
              </button>
              {designsOpen && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: isMobile() ? 180 : 360, overflowY: "auto" }}>
                  {designs[LAST_SLOT] && (
                    <button disabled={decorBusy} onClick={() => doApplySlot(LAST_SLOT)} style={uiBtn(UI.gold, { on: confirmApply === `S:${LAST_SLOT}` })}>
                      {confirmApply === `S:${LAST_SLOT}` ? "SURE? · " : ""}↶ PUT IT BACK · to “{designs[LAST_SLOT].name}”
                    </button>
                  )}
                  <div style={{ fontSize: 10, color: UI.dim }}>YOUR LAYOUTS · {layoutsOwned.length}/{layoutsCount}{layoutsOwned.length < layoutsCount ? " — Ally sells the rest, out in the yard" : ""}</div>
                  {layoutsOwnedPlanned.map((it) => (
                    <div key={it.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <LayoutMinimap itemId={it.id} width={96} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: UI.text }}>{it.name}{interiorNow.from === it.id ? " · BUILT" : ""}</div>
                        <button disabled={decorBusy || interiorNow.from === it.id} onClick={() => doApplyLayout(it.id)} style={{ ...uiBtn(UI.gold, { on: confirmApply === `L:${it.id}` }), marginTop: 4 }}>
                          {interiorNow.from === it.id ? "BUILT" : confirmApply === `L:${it.id}` ? "SURE? · APPLY" : "APPLY"}
                        </button>
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: 10, color: UI.dim, marginTop: 2 }}>YOUR DESIGNS</div>
                  <input value={designName} maxLength={DESIGN_NAME_MAX} onChange={(e) => setDesignName(e.target.value)}
                         placeholder="name the Hall as it is now…"
                         style={{ background: "rgba(0,0,0,.35)", border: `1px solid ${UI.edge}`, borderRadius: 5, color: UI.text, padding: "4px 7px", fontSize: 11 }} />
                  {[...DESIGN_SLOTS, COPIED_SLOT].map((slot) => {
                    const d = designs[slot];
                    if (slot === COPIED_SLOT && !d) return null;
                    return (
                      <div key={slot} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {d ? <InteriorMinimap hall={designHalls[slot]} base={getHallBase()} width={120} wornTint={ITEMS[holding.hallWall]?.finish?.tint} />
                           : <div style={{ width: 96, height: 18, border: `1px dashed ${UI.edge}`, borderRadius: 3 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: d ? UI.text : UI.dim }}>
                            {slot === COPIED_SLOT ? "COPIED · " : `${slot.toUpperCase()} · `}{d ? d.name : "empty"}
                          </div>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                            {slot !== COPIED_SLOT && <button disabled={decorBusy} onClick={() => doSaveSlot(slot)} style={uiBtn(UI.gold, { on: confirmApply === `O:${slot}` })}>{d ? (confirmApply === `O:${slot}` ? "SURE? · SAVE OVER" : "SAVE OVER") : "SAVE HERE"}</button>}
                            {d && <button disabled={decorBusy} onClick={() => doApplySlot(slot)} style={uiBtn(UI.gold, { on: confirmApply === `S:${slot}` })}>{confirmApply === `S:${slot}` ? "SURE? · APPLY" : "APPLY"}</button>}
                            {d && <button disabled={decorBusy} onClick={() => doClearSlot(slot)} style={uiBtn(UI.err, { on: confirmApply === `C:${slot}` })}>{confirmApply === `C:${slot}` ? "SURE?" : "CLEAR"}</button>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(Object.keys(interiorNow.walls).length + Object.keys(interiorNow.doors).length + Object.keys(interiorNow.floors).length > 0) && (
                    <button disabled={decorBusy} onClick={doApplyPlain} style={uiBtn(UI.dim, { on: confirmApply === "plain" })}>
                      {confirmApply === "plain" ? "SURE? · PLAIN HALL" : "PLAIN HALL · knock every wall down"}
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* ⭐ OW-42 · BUILD ROOMS — the Architect's tools */}
            <div style={{ borderTop: `1px solid ${UI.edge}`, padding: 9 }}>
              <button onClick={() => setArchOpen((v) => !v)}
                      style={{ ...uiLabel, marginBottom: archOpen || archTool ? 6 : 0, background: "none", border: "none",
                               padding: 0, cursor: "pointer", color: UI.dim }}>
                {archOpen ? "▾" : "▸"} BUILD ROOMS · ✹{BUILD_COST_UD} a wall or doorway · edits free{archTool && !archOpen ? ` · ${archTool.toUpperCase()}` : ""}
              </button>
              {hallNow.broken && (
                <div style={{ fontSize: 10, color: "#f0b8b8", margin: "4px 0 6px", lineHeight: 1.5 }}>
                  Some of your walls shut part of the Hall off, so none are standing. They show faded red while a tool is out.
                  <button disabled={decorBusy} onClick={doClearSealing} style={{ ...uiBtn(UI.err), marginTop: 5, display: "block" }}>KNOCK DOWN THE WALLS THAT SHUT IT</button>
                </div>
              )}
              {archOpen && <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {[["wall", "▯ WALL"], ["door", "⌂ DOORWAY"], ["erase", "✕ KNOCK DOWN"], ["floor", "▭ PAINT FLOOR"], ["paint", "▮ PAINT WALLS"], ["deed", "✥ MOVE DEEDS"]].map(([t, label]) => (
                  <button key={t} disabled={decorBusy} aria-pressed={archTool === t} onClick={() => pickTool(t)} style={uiBtn(UI.gold, { on: archTool === t })}>{label}</button>
                ))}
              </div>}
              {archOpen && archTool && (archTool === "floor" || archTool === "wall" || archTool === "door" || archTool === "paint") && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6, maxHeight: isMobile() ? 64 : undefined, overflowY: "auto" }}>
                  <button onClick={() => setArchFinish(null)} style={uiBtn(UI.dim, { on: !archFinishFor(archTool) })}>
                    {archTool === "floor" ? "BARE (scrub)" : "THE HALL'S OWN"}
                  </button>
                  {(archTool === "floor" ? paintChoices.floor : paintChoices.wall).map((f) => (
                    <button key={f.id} onClick={() => setArchFinish(f.id)} style={{ ...uiBtn(f.finish?.tint || UI.gold, { on: archFinish === f.id }), display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: f.finish?.tint || UI.gold, border: `1px solid ${UI.edge}` }} />{f.name}
                    </button>
                  ))}
                </div>
              )}
              {archOpen && archTool && (
                <div style={{ fontSize: 10, color: UI.dim, marginTop: 6, lineHeight: 1.5 }}>
                  {archTool === "deed"
                    ? (heldDeed ? "Face or click where it goes. Face the north wall (or click it) to hang a painting." : "Face a deed and press E, or click it. Walls and layouts slide deeds aside by themselves.")
                    : `E or ${isMobile() ? "tap" : "click"} builds on the tile. ${isMobile() ? "LINE" : "SHIFT-click"} draws a straight run from the last one. Z undoes.`}
                </div>
              )}
              {archTool && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                  {archTool !== "deed" && <button aria-pressed={archLine} onClick={() => setArchLine((v) => !v)} style={uiBtn(UI.gold, { on: archLine })}>⟷ LINE</button>}
                  <button disabled={decorBusy || !archUndoN} onClick={doArchUndo} style={uiBtn(UI.gold)}>UNDO · Z</button>
                  {archTool === "deed" && <button disabled={decorBusy} onClick={doResetDeeds} style={uiBtn(UI.err, { on: confirmHome })}>{confirmHome ? "SURE? · EVERY DEED HOME" : "EVERY DEED HOME"}</button>}
                  <button onClick={() => pickTool(archTool)} style={uiBtn(UI.dim)}>DONE</button>
                </div>
              )}
            </div>
            {/* ⭐ THE FINISHES — floor and walls, worn like a sky */}
            <div style={{ borderTop: `1px solid ${UI.edge}`, padding: 9 }}>
              {/* ⚠ folds (E2E review): ten chips outside the scroll area squeezed
                  the phone shed's piece list to nothing */}
              <button onClick={() => setLooksOpen((v) => !v)}
                      style={{ ...uiLabel, marginBottom: looksOpen ? 6 : 0, background: "none", border: "none",
                               padding: 0, cursor: "pointer", color: UI.dim }}>
                {looksOpen ? "▾" : "▸"} FLOOR, WALLS &amp; LIGHT
              </button>
              {looksOpen && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxHeight: isMobile() ? 110 : undefined, overflowY: "auto" }}
                   onMouseLeave={() => setTryOn(null)}>
                {finishes.map((f) => {
                  const on = holding.hallFloor === f.id || holding.hallWall === f.id || holding.hallLight === f.id;
                  const have = !!owned[f.id] || f.priceUD === 0;
                  const swatch = f.finish?.tint || moodOf(f.id)?.key?.[0] || UI.gold;
                  const glyph = f.kind === KIND.HALL_FLOOR ? "▭" : f.kind === KIND.HALL_WALL ? "▯" : "♥";
                  return (
                    <button key={f.id} disabled={decorBusy || on} title={have ? f.blurb : `${f.name} — at Ally's · hover to try it on`}
                            onPointerEnter={(ev) => { if (ev.pointerType === "mouse") setTryOn(have ? null : f.id); }}
                            onClick={() => (have ? doWearFinish(f.id) : setTryOn((cur) => (cur === f.id ? null : f.id)))}
                            style={{ ...uiBtn(swatch, { on }), opacity: have ? 1 : 0.62,
                                     display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 12, height: 12, borderRadius: f.kind === KIND.HALL_LIGHT ? "50%" : 3, background: swatch, border: `1px solid ${UI.edge}` }} />
                      {glyph} {f.name}{have ? "" : " · ✹"}
                    </button>
                  );
                })}
              </div>}
              {tryOn && <div style={{ fontSize: 10, color: UI.dim, marginTop: 5 }}>
                Trying on {ITEMS[tryOn]?.name} — Ally sells it in the yard.
              </div>}
            </div>
          </>}
        />
      )}
      {isOwnHome && !anyModal && (
        <OnboardCard uid={viewerUid} room="hall" title="YOUR HALL OF DEEDS"
          onDismiss={() => markOnboarded(viewerUid, HALL_NEW_CARD)} lines={[
          ["WALK", isMobile() ? "the D-pad, bottom right" : "WASD or the arrows · one tap is one tile · drag to look around"],
          ["⚒ FURNISH", "opens the shed · pick a piece, it lands on the tile you face"],
          [isMobile() ? "TAP" : "CLICK", "a tile to aim the piece there · a piece to select it"],
          ["E", "puts a piece down · moves a selected piece · collects · goes through a door"],
          ["R · Q · Z", "turn · pick up · undo"],
          ["ROOMS", "screens and partitions are walls · an arch is a way through · rugs lie under furniture"],
          ["WALLS", "hangings go on plain stone — face the north wall, or the west end · gold tiles show where"],
          ["Q / ENTER", "inspect a deed, open the Deed Book, hang a frame"],
        ]} />
      )}
      {/* ⭐ OW-34 · what is NEW, for a player who dismissed the first card before it existed */}
      {isOwnHome && !anyModal && (
        <OnboardCard uid={viewerUid} room={HALL_NEW_CARD} after="hall" title="NEW IN YOUR HALL" lines={[
          ["BUILD ROOMS", "⚒ FURNISH › BUILD ROOMS — walls and doorways (✹1 a tile), paint for floor and walls (free). E or a click builds; SHIFT-click draws a line; Z undoes"],
          ["MOVE DEEDS", "the same panel — pick a deed up, set it anywhere; face the north wall to hang a painting"],
          ["LAYOUTS", "Ally sells whole interiors · ⚒ FURNISH › LAYOUTS & DESIGNS builds one in a press · PUT IT BACK swaps it back"],
          ["DESIGNS", "save your Hall in three slots · visiting a friend? ⎘ COPY THIS HALL takes their walls home"],
          ["ROOMS & WALLS", "screens and partitions are furniture walls · rugs lie under furniture · hangings go on plain stone"],
          ["LIGHT", "⚒ FURNISH › FLOOR, WALLS & LIGHT — hover a light you don't own to see it first"],
        ]} />
      )}

      {/* ⚠ BUTTONS, NOT A LEGEND. A phone has no E, no R and no ESC, so a
          hint bar listing three keyboard keys is a feature that simply
          does not exist on mobile — and the Hall already ships a D-pad,
          which means somebody IS going to furnish a room with their
          thumbs. The keys still work; these are the same three actions
          with something to tap. Lifted clear of the D-pad on mobile. */}
      {isOwnHome && ghostItem && (
        <div style={{
          // ⚠⚠ A VERTICAL LIFT CANNOT CLEAR A BOTTOM-RIGHT D-PAD.
          // This was `bottom: 104, left: 50%, translateX(-50%)` — but
          // `MobileDpad` is 154×154 at `bottom:24 right:20` on z-200, so
          // on a 400px phone it owns x = 226→380 and y = vh−178→vh−24.
          // A centred bar at vh−137 sits INSIDE that band: you reached
          // for DROP IT and walked west instead. Getting out of the way
          // is a horizontal problem, not a vertical one. Found by
          // adversarial review 2026-09-13.
          position: "fixed", zIndex: 160,
          ...(isMobile()
            ? { left: 12, right: 190, bottom: furnishing ? ABOVE_SHED_MOBILE : (is3D ? 64 : 20), flexWrap: "wrap" }
            : { left: "50%", bottom: is3D ? 56 : 18, transform: "translateX(-50%)" }),
          display: "flex", alignItems: "center", gap: 8,
          background: "rgba(5,3,20,0.92)",
          border: `1px solid ${ghostBlocked ? "#e8453c" : "#2c3a22"}`,
          borderRadius: 8, padding: "6px 10px", fontSize: 10, letterSpacing: 1,
          fontFamily: SD.fontDisplay, color: SD.starSilver,
          // ⚠ NOT nowrap on mobile — a bar that cannot reflow bleeds off
          // both edges at 360px.
          whiteSpace: isMobile() ? "normal" : "nowrap",
        }}>
          <button disabled={ghostBlocked || decorBusy} onClick={doPlaceDecor} style={{
            background: `${(GHOST_VERB[ghost?.state || "free"]).tint}22`,
            border: `1px solid ${(GHOST_VERB[ghost?.state || "free"]).tint}`,
            borderRadius: 5, padding: "5px 11px",
            color: (GHOST_VERB[ghost?.state || "free"]).tint,
            cursor: ghostBlocked || decorBusy ? "default" : "pointer",
            fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>{ghost?.state === "blocked" ? `CAN'T · ${ghost.reason}` : ghost?.state === "stack" ? `BALANCE ON THE ${String(ghost.onName).toUpperCase()}` : movingPropId ? "SET IT DOWN" : "PUT IT DOWN"}</button>
          <button disabled={decorBusy || ghostIsWall} onClick={turnHeld} title={ghostIsWall ? "A hanging faces the room" : undefined} style={{
            background: "rgba(255,255,255,0.04)", border: `1px solid ${SD.void4}`,
            borderRadius: 5, padding: "5px 11px", color: SD.starSilver,
            cursor: "pointer", fontSize: 10, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>TURN</button>
          <button onClick={() => { setHeldItem(null); setMoving(null); setAim(null); }} style={{
            background: "none", border: `1px solid ${SD.void4}`,
            borderRadius: 5, padding: "5px 11px", color: SD.starDim,
            cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>DROP IT</button>
          <span style={{ color: SD.starDim, fontSize: 10 }}>E · R · ESC · {isMobile() ? "tap" : "click"} a tile to aim</span>
        </div>
      )}

      {archOn && archVerdict && !anyModal && (
        <div style={{
          position: "fixed", zIndex: 160,
          ...(isMobile()
            ? { left: 12, right: 190, bottom: ABOVE_SHED_MOBILE, flexWrap: "wrap" }
            : { left: "50%", bottom: 56, transform: "translateX(-50%)" }),
          display: "flex", alignItems: "center", gap: 8,
          background: "rgba(5,3,20,0.92)", border: `1px solid ${archVerdict.ok ? "#3a5a2e" : "#e8453c"}`,
          borderRadius: 8, padding: "6px 10px", fontSize: 10, letterSpacing: 1,
          fontFamily: SD.fontDisplay, color: SD.starSilver, whiteSpace: isMobile() ? "normal" : "nowrap",
        }}>
          <button disabled={!archVerdict.ok || decorBusy} onClick={() => doArchAt(archAt)} style={{
            background: archVerdict.ok ? "rgba(154,212,106,0.14)" : "rgba(232,69,60,0.12)",
            border: `1px solid ${archVerdict.ok ? "#9ad46a" : "#e8453c"}`, borderRadius: 5, padding: "5px 11px",
            color: archVerdict.ok ? "#9ad46a" : "#f0b8b8", cursor: archVerdict.ok ? "pointer" : "default",
            fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>{archVerdict.ok ? archVerdict.label : `CAN'T · ${archVerdict.why}`}</button>
          {heldDeed && <button onClick={() => setHeldDeed(null)} style={{
            background: "none", border: `1px solid ${SD.void4}`, borderRadius: 5, padding: "5px 11px",
            color: SD.starDim, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>PUT IT BACK</button>}
          <button disabled={decorBusy || !archUndoN} onClick={doArchUndo} style={{
            background: "none", border: `1px solid ${SD.void4}`, borderRadius: 5, padding: "5px 11px",
            color: SD.starSilver, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>UNDO</button>
          <button onClick={() => pickTool(archTool)} style={{
            background: "none", border: `1px solid ${SD.void4}`, borderRadius: 5, padding: "5px 11px",
            color: SD.starDim, cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: SD.fontDisplay,
          }}>DONE</button>
          {!isMobile() && <span style={{ color: SD.starDim, fontSize: 10 }}>E · Z · ESC</span>}
        </div>
      )}

      {decorFlash && (
        <div style={{
          position: "fixed", top: 58, left: "50%", transform: "translateX(-50%)",
          zIndex: 165, pointerEvents: "none", maxWidth: 460,
          background: "rgba(5,3,20,0.94)",
          border: `1px solid ${decorFlash.ok ? "#3a5a2e" : "#5a2e2e"}`,
          borderRadius: 8, padding: "7px 14px", fontSize: 10, lineHeight: 1.6,
          fontFamily: SD.fontDisplay,
          color: decorFlash.ok ? "#d8f0a8" : "#f0b8b8",
        }}>{decorFlash.text}</div>
      )}

      {isMobile() && <MobileDpad keysRef={keysRef} />}

      {/* Trophy inspect */}
      {inspect && (
        <TrophyInspect
          trophy={inspect}
          earned={earnedOf(inspect.id)}
          record={deedMap[inspect.id]}
          onClose={() => setInspect(null)}
        />
      )}

      {/* Full catalog — the BadgeBook, viewingSelf iff it's your own hall */}
      {showBook && (
        <AchievementBadgeBook
          uid={ownerUid}
          viewingSelf={isOwnHome}
          playerName={ownerName}
          onClose={() => setShowBook(false)}
        />
      )}

      {/* M1 — Hang picker (own hall, empty frame) */}
      {pickerSlot && isOwnHome && (
        <FramePicker
          slot={pickerSlot}
          galleryById={galleryById}
          hungPieceIds={hungPieceIds}
          onHang={(pieceId) => { hangPiece(pickerSlot.slotId, pieceId); setPickerSlot(null); }}
          onClose={() => setPickerSlot(null)}
        />
      )}

      {/* M1 — Frame inspect (hung piece; Take Down only in own hall) */}
      {frameInspect && (
        <FrameInspect
          piece={frameInspect.piece}
          isOwnHome={isOwnHome}
          onTakeDown={() => { takeDown(frameInspect.slotId); setFrameInspect(null); }}
          onClose={() => setFrameInspect(null)}
        />
      )}
    </div>
  );
}
